import { EventEmitter } from 'eventemitter3';
import {
    IApplyCouponByIdParams, IQualifyItem, IQualifyItemExt, IRemovedCoupon,
    ITotalCouponPrice, IvItem, IAddCouponToOrderParams, ICheckCouponHasQualifyingItemsParams, BeforeTaxDiscountState
} from "./coupons.interface";
import { IUserCampaignCodesData, IUserLoyaltyReward } from "./user.interface";
import { IIncompleteDiscount, IOrder, IOrderChallenge, IOrderChallengeMember, IOrderCoupon, IOrderItem, IOrderItemCoupon, IOrderItemModifier } from "./order.interface";
import { IDataChallenge, IDataChallengeMember, IDataCoupon, IDataItem, IItemModifierDisplayGroupModifier, IItemPrice } from "./data.interface";
import { ICloudCoupon } from "./context.interface";
import { Util } from "../utils/util";
import { CouponIdList, CouponLimitPerOrder, CouponTypeList, StoreCouponLimitType } from "../types/enums";
import { DialogCreators } from "../utils/dialog-creators";
import { OnlineOrderingUtil } from "../online-ordering/online-ordering-util";
import { ItemPricingUnavailableError } from "../online-ordering/errors/item-pricing-unavailable-error";
import { Data } from "./data";
import { User } from "./user";
import { ILoyaltyProvider } from "../services/loyalty-provider/loyalty-provider.interface";
import { hasCouponCountedByLimit, isExemptFromOnlineCouponLimit, isOverride207Coupon } from "../services/loyalty-provider/coupon-limit";
import { DataLayerService } from "../services/data-layer-service";
import { OnlineOrderingService } from "../services/online-ordering-service";
import { IGetItemTaxDependencies } from "../online-ordering/interfaces/online-ordering.interface";
import { EventTypes } from '../core/event-bus';
import { TimedMessage } from '../components/shared/TimedMessage/timed-message';
import { Context } from "./context";
import { LocalizationService } from "../services/localization-service";
import { IDealBuilderCouponDTO } from '../components/modals/DealBuilder/deal-builder.interface';

// 2DO: Finish data conversion
const CalculationMethod = {
    IncludeAllWithDiscountedPrice: 0,
    IncludeAllWithOriginalPrice: 1,
    ExcludeItemsWithDiscountsOrLoyalty: 2,
    ExcludeDiscountedExceptLoyaltyWithOriginalPrice: 3,
    ExcludeDiscountedExceptLoyaltyWithDiscountedPrice: 4
};

export class Coupon {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _user: User;
    private readonly _dataLayer: DataLayerService;
    private readonly _orderingService: OnlineOrderingService;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, user: User, dataLayer: DataLayerService, orderingService: OnlineOrderingService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._user = user;
        this._dataLayer = dataLayer;
        this._orderingService = orderingService;
    }

    public static SucValidationNeeded (isPromoCode: boolean, couponCode: string | null): boolean {
        return couponCode != null && couponCode.length > 0 && (isPromoCode || couponCode.toLowerCase().includes("suc_"));
    }

    public static SanitizeSucCouponCode (couponCode: string | null | undefined): (string | null) {
        return couponCode ? couponCode.toLowerCase().replace(/^suc_/, "").trim() : null;
    }

    public static IsCampaignOrPromoCode(orderCoupon: IOrderCoupon | undefined): boolean {
        return orderCoupon ? (orderCoupon.IsCampaignCode || orderCoupon.IsPromotionCode) : false;
    }

    public RemoveCouponAll = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider): Promise<IRemovedCoupon[]> => {
        let removedCoupons: IRemovedCoupon[] = [];
        removedCoupons = await this.RemoveCouponByItemKey(order, incompleteDiscounts, loyaltyProvider, null, true, false);

        await this.AutoApplyCoupons(order, incompleteDiscounts, loyaltyProvider);

        return removedCoupons;
    }

    public RemoveCouponByItemKey = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, itemKey: number | null, refresh: boolean = false, autoApply: boolean = false): Promise<IRemovedCoupon[]> => {
        const itemsCoupons = order.ItemsCoupons;
        const removedCouponKeySet = new Set<number>();
        order.ItemsCoupons = itemsCoupons.filter((iCoupon) => {
            if (!iCoupon) return false;

            const shouldRemove = itemKey === null || iCoupon.ItemKey === itemKey;
            if (!shouldRemove) return true;

            removedCouponKeySet.add(iCoupon.CouponKey);

            const oCoupon = order.Coupons.find(c => c.CouponKey === iCoupon.CouponKey);
            const isLoyaltyReward = oCoupon?.RewardId != null;

            this._removeDiscountFromItem(
                order,
                iCoupon.ItemKey,
                iCoupon.HalfIndex,
                iCoupon.BeforeTaxDiscount,
                iCoupon.AfterTaxDiscount,
                iCoupon.DiscountedQuantity === 1,
                isLoyaltyReward
            );

            return false; // remove from ItemsCoupons
        });

        const removedCouponKeys = [...removedCouponKeySet];
        if (removedCouponKeys.length === 0) return [];

        const removedCouponIDs = this._buildRemovedCoupons(order, removedCouponKeys);

        for (const couponKey of removedCouponKeys) {
            await this.RemoveCouponByKey(order, incompleteDiscounts, loyaltyProvider, couponKey, refresh, autoApply);
        }

        return removedCouponIDs;
    }

    private readonly _buildRemovedCoupons = (order: IOrder, removedCouponKeys: number[]): IRemovedCoupon[] => {
        // Build lookup maps once
        const couponByKey = new Map<number, IOrderCoupon>();
        for (const c of order.Coupons) couponByKey.set(c.CouponKey, c);

        const rewards = this._user.getProperty("LoyaltyData")?.Rewards ?? [];
        const rewardIdSet = new Set<number>(rewards.map(r => r.RewardId));

        const removedCouponIDs: IRemovedCoupon[] = [];
        for (const couponKey of removedCouponKeys) {
            const coupon = couponByKey.get(couponKey);
            if (!coupon) continue;

            const rewardIdNum = coupon.RewardId != null ? Number(coupon.RewardId) : NaN;
            const rewardId = rewardIdSet.has(rewardIdNum) ? rewardIdNum : null;

            removedCouponIDs.push({
                couponId: coupon.CouponId,
                offerCode: coupon.CouponCode || null,
                channelId: coupon.ChannelID,
                rewardId,
                isBankedCurrency: coupon.IsBankedCurrency,
                isPromotionCode: coupon.IsPromotionCode,
                isCampaignCode: coupon.IsCampaignCode,
                sourceId: coupon.SourceId,
                isSpecialCode: coupon.IsSpecialCode || false,
                isSingleUseCoupon: coupon.IsSingleUseCoupon || false,
                autoApplied: coupon.AutoApplied || false,
            });
        }

        return removedCouponIDs;
    }

    private readonly _removeDiscountFromItem = (order: IOrder, itemKey: number, halfIndex: number, beforeTaxDiscount: number, afterTaxDiscount: number, clearDiscountedQuantity: boolean, isLoyaltyReward: boolean): number => {
        let arrayIndex = -1;
        const itemsOrder = order.Items;
        const itemsData = this._data.getProperty("Items") || []
        for (const [index, oItem] of itemsOrder.entries()) {
            if (oItem.ItemKey === itemKey && oItem.HalfIndex === halfIndex) {
                oItem.BeforeTaxDiscount -= beforeTaxDiscount;
                oItem.AfterTaxDiscount -= afterTaxDiscount;
                if (isLoyaltyReward) {
                    oItem.LoyaltyDiscountAmount = Util.Float2(oItem.LoyaltyDiscountAmount - Util.Float2(beforeTaxDiscount + afterTaxDiscount));
                }
                if (clearDiscountedQuantity) {
                    if (oItem.DiscountedQuantity > 0)
                        oItem.DiscountedQuantity -= 1;
                    if (oItem.LoyaltyDiscountsQTY > 0)
                        oItem.LoyaltyDiscountsQTY -= 1;
                    if (oItem.NonLoyaltyDiscountsQTY > 0)
                        oItem.NonLoyaltyDiscountsQTY -= 1;
                }
                const mItem = itemsData.find(x => x.ItemId === oItem.ItemId) || null;

                const dependencies = {
                    taxesData: this._data.getProperty("Taxes") || [],
                    zipTaxesData: this._data.getProperty("ZipTaxes") || [],
                    isCallCenter: this._data.getProperty("Settings")?.ISCC || false,
                    orderTypeId: order.OrderTypeID,
                    address: order.Address
                };
                OnlineOrderingUtil.TaxItem(mItem, oItem, dependencies);
                arrayIndex = index;
            } else if (oItem.ItemKey === itemKey && oItem.HalfCount == 1)
                if (clearDiscountedQuantity && oItem.DiscountedQuantity > 0) {
                    oItem.DiscountedQuantity -= 1;
                }
        }
        return arrayIndex;
    }

    public RemoveCouponByKey = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponKey: number, refresh: boolean, autoApply: boolean): Promise<void> => {
        let i = order.ItemsCoupons.length;
        let itemKey = 0;
        while (i--) {
            const iCoupon = order.ItemsCoupons[i];
            if (iCoupon.CouponKey === couponKey) {
                itemKey = iCoupon.ItemKey;
                const oCoupon = order.Coupons.find(c => c.CouponKey === iCoupon.CouponKey);
                const isLoyaltyReward = oCoupon?.RewardId != null;
                this._removeDiscountFromItem(order, iCoupon.ItemKey, iCoupon.HalfIndex, iCoupon.BeforeTaxDiscount, iCoupon.AfterTaxDiscount, (iCoupon.DiscountedQuantity === 1), isLoyaltyReward);
                order.ItemsCoupons.splice(i, 1);
            }
        }

        for (let i = 0; i < order.Coupons.length; i++) {
            if (order.Coupons[i].CouponKey !== couponKey)
                continue;

            const couponCode = order.Coupons[i].CouponCode;

            if (order.Coupons[i].CouponId === 7 && itemKey !== 0)
               this._unRedeemItem(order, itemKey);

            // invalidate if single use coupon
            if (couponCode.toLowerCase().includes("suc_"))
                await this._invalidateSingleUseCoupon(order, couponCode);

            order.Coupons.splice(i, 1);
            break;
        }

        if (refresh)
            this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, couponKey);
        if (autoApply)
            await this.AutoApplyCoupons(order, incompleteDiscounts, loyaltyProvider);
    }

    private _unRedeemItem(order: IOrder, itemKey: number): void {
        order.Items.forEach(item => {
            if (item.ItemKey === itemKey) {
                item.IsRedeemed = false;
            }
        });
    }

    public ApplyCouponByID = async (params: IApplyCouponByIdParams, eventBus?: EventEmitter): Promise<boolean | null> => {
        const {
            order,
            incompleteDiscounts,
            loyaltyProvider,
            couponId,
            discount = null,
            showErrorMsg = false,
            showApplyMsg = false,
            couponCode = null,
            itemKey = null,
            channelId = null,
            autoApplying = false,
            isCampaignCode = false,
            isPromotionCode = false,
            rewardId = null,
            runBatch = true,
            isBankedCurrency = false,
            sourceId = null,
            isSUCValidationReq = false,
            isSpecialCode = false,
            isSingleUseCoupon = false
        } = params;

        const coupon = this.GetCoupon(couponId, true, order.OrderTypeID, channelId);

        if (order.Total != 0) {
            if (!coupon)
                return false;

            if (couponId <= 11 && discount) {
                coupon.MaximumDiscountAmount = discount;
                coupon.Discount = discount;
            }

            const applied = await this.ApplyCoupon(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, showErrorMsg, showApplyMsg, itemKey, autoApplying, isCampaignCode, isPromotionCode, rewardId, eventBus, sourceId, runBatch, isBankedCurrency, isSUCValidationReq, isSpecialCode, isSingleUseCoupon);
            if (applied)
                this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, coupon);
            return applied;
        } else if (showErrorMsg) {
            if (coupon) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService
                        .names("CPNMINAMT")
                        .replace("??", coupon.MinimumOrderAmount.toFixed(2)),
                    true
                );
            } else {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("CannotUseCouponTotalZero"),
                    true
                );
            }

        }

        return false;
    }

    public ApplyCoupon = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, coupon: IDataCoupon, couponCode: string | null, showErrorMsg: boolean, showApplyMsg: boolean, itemKey?: number | null, autoApplying?: boolean, isCampaignCode?: boolean, isPromotionCode?: boolean, rewardId?: number | null, eventBus?: EventEmitter | null, sourceId?: number | null, runBatch: boolean = true, isBankedCurrency: boolean = false, isSUCValidationReq: boolean = false, isSpecialCode: boolean = false, isSingleUseCoupon: boolean = false): Promise<boolean | null> => {
        let reward: IUserLoyaltyReward | null = null;
        const loyaltyData = this._user.getProperty("LoyaltyData");
        if (loyaltyData && loyaltyData.Rewards.length > 0)
            if (rewardId && !couponCode)
                reward = loyaltyData.Rewards.find(x => x.RewardId === rewardId) || null;
            else
                reward = loyaltyData.Rewards.find(x => x.RewardId === Number(couponCode)) || null;

        let offer: IUserCampaignCodesData | null = null;
        const offers = this._user.getProperty("Offers");
        if ((isCampaignCode || isPromotionCode) && offers && offers.Codes.length > 0)
            offer = offers.Codes.find(x => x.CouponCode.toLowerCase() === Coupon.SanitizeSucCouponCode(couponCode)) || null;

        let credit = null;
        if (sourceId === 1)
            credit = this._user.getProperty("NextOrderCredits")?.find(x => x.CouponID === coupon.CouponId && x.CustomerCreditID.toString() === couponCode) || null;

        if (!this.VerifyCouponDate(order, coupon)) {
            if (showErrorMsg) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("InvalidCouponDate"),
                    true
                );
            }
            return false;
        }

        if (!this.isCouponAllowedInChallengeEvent(order, showErrorMsg))
            return false;

        if (isPromotionCode && couponCode && isSUCValidationReq) {
            const tempOrderDate = order.FutureDate ?? order.OrderStartTime;
            const orderDate = new Date(new Date(Util.deepClone(tempOrderDate)).setHours(0, 0, 0, 0));
            const validCouponCode = await this._redeemSingleUseCouponAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, orderDate, showErrorMsg);
            if (validCouponCode === null) {
                if (offer) offer.Used = false;

                return false;
            } else 
                couponCode = validCouponCode;
        }

        if (coupon.IsScheduled && !this.CheckCouponSCHS(order, coupon)) {
            if (showErrorMsg) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("InvalidScheduleCode"),
                    true
                );
            }
            return false;
        }

        if (reward?.ExpiryDate && !loyaltyProvider.discountValidForOrderTime(reward.ExpiryDate)) {
            if (showErrorMsg) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("InvalidReward"),
                    true
                );
            }
            return false;
        }

        if (coupon.CouponId !== CouponIdList.LOYALTY_REDEEM_ITEM && !reward && !this._checkOneCouponOnly(order, coupon, autoApplying))
            return null;

        if (!couponCode) couponCode = ""; //TODO
        let returnValue = true;
        let isActive = true;
        let couponKey = 0;
        let discountAmount = 0.00;
        let qualifiedList: IvItem[] = [];

        if (!this.checkMinimumOrderAmount(order, coupon, autoApplying || false, false, showErrorMsg)) {
            if (!incompleteDiscounts.some(cpn => cpn.couponId === coupon.CouponId)) {
                incompleteDiscounts.push({ couponId: coupon.CouponId, couponCode: couponCode, channelId: null, isCampaignCode: isCampaignCode, isPromotionCode: isPromotionCode, sourceId: sourceId, isSpecialCode: isSpecialCode || false });
            }
            return false;
        }

        if (!this._checkCouponLimit(order, coupon, autoApplying))
            return false;

        // Get Qualifying Items for the given coupon
        const qualifyingCoupon = reward ? { ...coupon, IsLoyaltyReward: true } : coupon;
        qualifiedList = this._getQualifiedItemList(order, qualifyingCoupon, itemKey);

        if (qualifiedList?.length > 0) {
            discountAmount = await this._getCouponDiscountAmount(order, coupon, qualifiedList);

            if (discountAmount > 0) {
                this._addDiscountsToItems(order, qualifiedList, coupon.IsPriceReducer, reward !== null);

                const addCouponParams: IAddCouponToOrderParams = {
                    couponId: coupon.CouponId,
                    couponCode: couponCode,
                    discount: coupon.Discount,
                    discountAmount: discountAmount,
                    isBeforeTax: coupon.IsBeforeTax,
                    isActive: isActive,
                    allowOtherCoupons: coupon.AllowOtherCoupons,
                    excludeLoyaltyUsage: coupon.ExcludeLoyaltyUsage,
                    rewardId: rewardId || null,
                    channelId: coupon.ChannelID || null,
                    isCampaignCode: isCampaignCode || false,
                    isPromotionCode: isPromotionCode || false,
                    isBankedCurrency: isBankedCurrency,
                    isAutoApplying: autoApplying || false,
                    sourceId: sourceId ?? null,
                    isSpecialCode: isSpecialCode || false,
                    isSingleUseCoupon: isSingleUseCoupon
                }
                couponKey = this._addCouponToOrder(order, addCouponParams);
                this._addCouponToItemsCoupon(order, qualifiedList, couponKey, coupon.Priority, isActive, (coupon.AllowOtherCoupons ? 0 : 1));

                if (this._eventBus)
                    this._eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, coupon);

                if (coupon.AllowOtherCoupons)
                    this._clearMarkedItems(order);
                else
                    this._useMarkedItems(order);
            } else {
                if (showErrorMsg)
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        `${this._localizationService.names(
                            "CouponNotQualified"
                        )}</br>${this._localizationService.GetDescription(
                            coupon.Descriptions
                        )}`,
                        true
                    );
                returnValue = false;
            }
        } else {
            if (showErrorMsg) {
                if (coupon.Descriptions)
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        `${this._localizationService.names(
                            "CouponNotQualified"
                        )}</br>${this._localizationService.GetDescription(
                            coupon.Descriptions
                        )}`,
                        true
                    );
                else
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        this._localizationService.names("CouponNotQualified"),
                        true
                    );
            }
            returnValue = false;
        }

        if (returnValue) {
            // Check For Minimum Quantity Once more
            if (!this.checkMinimumOrderAmount(order, coupon, autoApplying || false, true, showErrorMsg)) {
                if (!incompleteDiscounts.some(cpn => cpn.couponId === coupon.CouponId)) {
                    incompleteDiscounts.push({
                        couponId: coupon.CouponId,
                        couponCode: couponCode,
                        channelId: null,
                        isCampaignCode: isCampaignCode,
                        isPromotionCode: isPromotionCode,
                        rewardId: reward ? reward.RewardId : null
                    });
                    order.Coupons = order.Coupons.filter(cpn => cpn.CouponId !== coupon.CouponId);
                }
                await this.RemoveCouponByKey(order, incompleteDiscounts, loyaltyProvider, couponKey, false, false);
                if (eventBus) {
                    eventBus.emit(EventTypes.ORDER_COUPON_CHANGED, coupon);
                }

                if (credit)
                    credit.Used = true;

                return false;
            }

            if (showApplyMsg) {
                const msg = new TimedMessage(this._localizationService.names("CouponApplied"));
                msg.show();
            }

            if (reward)
                reward.Used = true;
            else if (offer)
                offer.Used = true;
            else if (credit)
                credit.Used = true;

            if (runBatch)
                loyaltyProvider.batchComparison(true);
        }

        return returnValue;
    }

    private _hasEventBlockingCoupons(order: IOrder): { block: boolean; eventName?: string } {
        if (!order.Challenges.length) return { block: false };

        const challenges = this._data.getProperty("Challenges");

        for (const orderChallenge of order.Challenges) {
            const challenge = challenges.find(x => x.ChallengeId === orderChallenge.challengeId);
            if (!challenge) continue;

            const result = this._findBlockingEventInChallenge(orderChallenge, challenge);
            if (result.block) return result;
        }

        return { block: false };
    }

    private _findBlockingEventInChallenge(orderChallenge: IOrderChallenge, challenge: IDataChallenge): { block: boolean; eventName?: string } {
        for (const orderMember of orderChallenge.members) {
            const member = challenge.Members.find(x => x.MemberId === orderMember.memberId);
            if (!member) continue;

            const result = this._findBlockingEventInMember(orderMember, member);
            if (result.block) return result;
        }

        return { block: false };
    }

    private _findBlockingEventInMember(orderMember: IOrderChallengeMember, member: IDataChallengeMember): { block: boolean; eventName?: string } {
        for (const orderEvent of orderMember.events) {
            const event = member.Events.find(x => x.EventId === orderEvent.eventId);
            if (event && !event.IsAllowCoupons)
                return { block: true, eventName: event.EventName };
        }

        return { block: false };
    }

    private async _setHalfNHalfUpgradePrice(order: IOrder, coupon: IDataCoupon, qualifiedList: IvItem[]): Promise<number> {
        let highestPrice = 0.0;
        let averagePrice = 0.0;
        for (const qualifiedItem of qualifiedList) {
            const oItem = order.Items[qualifiedItem.ItemArrayIndex];
            for (const upgrade of coupon.Upgrades) {
                if (oItem.SizeId === upgrade.BuySizeId) {
                    const mItem = this._data.getProperty("Items")[oItem.Index];
                    const orderTypePriceGroupId = order.OrderTypeSubType.PriceGroupId;
                    const defaultPriceGroup = this._data.getProperty("DefaultPriceGroupId");
                    const nonChargeableDisplayGroups = this._data.getProperty("Settings").NCDG;
                    const preModifiers = this._data.getProperty("PreModifiers");
                    let price: number;
                    try {
                        price = OnlineOrderingUtil.GetItemPrice(mItem, oItem.SizeId, oItem.Modifiers, orderTypePriceGroupId, defaultPriceGroup, nonChargeableDisplayGroups, preModifiers).Price;
                    } catch (e) {
                        if (!(e instanceof ItemPricingUnavailableError))
                            throw e;
                        continue;
                    }
                    if (price < 0)
                        price = -price;

                    if (price > highestPrice)
                        highestPrice = price;

                    averagePrice += price;
                    break;
                }
            }
        }

        if (this._data.getProperty("Settings").HHP === 1) {
            return highestPrice;
        } else if (this._data.getProperty("Settings").HHP === 2) {
            return Util.Float2(averagePrice / Util.Float2(qualifiedList.length));
        }

        return 0.0;
    }

    private readonly _checkOneCouponOnly = (order: IOrder, coupon: IDataCoupon, autoApplying?: boolean): boolean => {
        if (coupon.LimitsPerOrder === CouponLimitPerOrder.OVERRIDE_207)
            return true;

        const catalog = this._data.getProperty("Coupons") ?? [];
        const coupons = order.Coupons.filter(x => x.CouponId != CouponIdList.LOYALTY_REDEEM_ITEM && !isOverride207Coupon(catalog, x.CouponId));
        if (coupons.length > 0) {
            const oneCouponLimit = this._data.getProperty("Settings").CPNLIM;
            if (oneCouponLimit === StoreCouponLimitType.ONLINE_ONLY || oneCouponLimit === StoreCouponLimitType.BOTH) {
                !autoApplying &&
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        this._localizationService.names("CpnLimitOne"),
                        true
                    );
                return false;
            }
        }
        return true;
    }

    public checkMinimumOrderAmount = (order: IOrder, coupon: IDataCoupon, autoApplying: boolean, postApplyCheck: boolean, showErrorMsg: boolean): boolean => {
        if (coupon.MinimumOrderAmount > 0) {
            const orderAmount = this._getOrderAmountForMinimumCheck(order, coupon, postApplyCheck);

            if (orderAmount < coupon.MinimumOrderAmount) {
                !autoApplying &&
                    showErrorMsg &&
                    DialogCreators.messageBoxOk(
                        this._localizationService,
                        this._dataLayer,
                        this._localizationService
                            .names("CPNMINAMT")
                            .replace("??", coupon.MinimumOrderAmount.toFixed(2)),
                        true
                    );
                return false;
            }
        }
        return true;
    }

    private readonly _getOrderAmountForMinimumCheck = (order: IOrder, coupon: IDataCoupon, postApplyCheck: boolean): number => {
        this._calcDiscountedQTYs(order);

        // Bug 82792: when the coupon type is Percent Off Entire Order or Percentage Off All Items in Category
        // AND disallows other coupons on items, the "Include All" calculation methods (and the unset/default) must
        // exclude units discounted by OTHER coupons from the Minimum Order Amount total — in BOTH the pre-apply
        // and post-apply checks — while still counting this coupon's own units and loyalty-redeemed units.
        const appliesOnMultipleItems = coupon.CouponTypeId == CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER || coupon.CouponTypeId == CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY;
        if (!coupon.AllowOtherCoupons && appliesOnMultipleItems) {
            switch (coupon.CalculationMethodID) {
                case CalculationMethod.IncludeAllWithOriginalPrice:
                    return this._getIncludeAllExcludingOtherCoupons(order, coupon, false);
                case null:
                case undefined:
                case CalculationMethod.IncludeAllWithDiscountedPrice:
                    return this._getIncludeAllExcludingOtherCoupons(order, coupon, true);
            }
        }

        switch (coupon.CalculationMethodID) {
            case null:
            case undefined:
            case CalculationMethod.IncludeAllWithDiscountedPrice:
                return Util.Float2(order.SubTotal - order.Discount);
            case CalculationMethod.IncludeAllWithOriginalPrice:
                return order.SubTotal;
            case CalculationMethod.ExcludeItemsWithDiscountsOrLoyalty:
                // Bug 82855: exclude discounted items in BOTH the pre-apply and post-apply checks
                // (the old post-apply `SubTotal - Discount` shortcut excluded nothing). Post-apply,
                // a Percentage-Off-Entire-Order coupon has discounted every item, so the qualifying
                // total collapses to 0 and the coupon correctly fails its Minimum Order Amount —
                // regardless of the "Allow Other Coupons on Item" setting.
                return order.Items.reduce((sum, oItem) => sum + this._calcNonDiscountedItems(oItem), 0);
            case CalculationMethod.ExcludeDiscountedExceptLoyaltyWithOriginalPrice:
                return this._calcExclusiveLoyaltyTotal(order, () => 0);
            case CalculationMethod.ExcludeDiscountedExceptLoyaltyWithDiscountedPrice:
                return this._calcExclusiveLoyaltyTotal(order, oItem => oItem.LoyaltyDiscountAmount);
            default:
                return 0;
        }
    }

    // Bug 82792: sum the order for an "Include All" coupon that disallows other coupons on items,
    // excluding units discounted by OTHER (non-loyalty) coupons while keeping this coupon's own
    // units and loyalty-redeemed units. When useDiscountedPrice is true (IncludeAllWithDiscountedPrice
    // / unset) the kept units are counted at their discounted price by subtracting this coupon's own
    // discount and any loyalty discount; otherwise (IncludeAllWithOriginalPrice) at original price.
    private readonly _getIncludeAllExcludingOtherCoupons = (order: IOrder, coupon: IDataCoupon, useDiscountedPrice: boolean): number => {
        const ownCouponKeys = new Set(
            order.Coupons.filter(c => c.CouponId === coupon.CouponId).map(c => c.CouponKey)
        );
        const otherNonLoyaltyKeys = new Set(
            order.Coupons.filter(c => c.RewardId == null && !ownCouponKeys.has(c.CouponKey)).map(c => c.CouponKey)
        );

        let orderAmount = 0;
        for (const oItem of order.Items) {
            const itemCoupons = order.ItemsCoupons.filter(ic => ic.ItemKey === oItem.ItemKey && ic.HalfIndex === oItem.HalfIndex);
            const otherCouponQTY = itemCoupons.filter(ic => otherNonLoyaltyKeys.has(ic.CouponKey)).length;
            const keptQTY = oItem.Quantity - otherCouponQTY;
            if (keptQTY <= 0)
                continue;

            let itemAmount = Util.Float2(oItem.Price * keptQTY);
            if (useDiscountedPrice) {
                const ownDiscount = itemCoupons
                    .filter(ic => ownCouponKeys.has(ic.CouponKey))
                    .reduce((sum, ic) => Util.Float2(sum + ic.BeforeTaxDiscount + ic.AfterTaxDiscount), 0);
                itemAmount = Util.Float2(itemAmount - ownDiscount - (oItem.LoyaltyDiscountAmount ?? 0));
            }
            orderAmount = Util.Float2(orderAmount + itemAmount);
        }
        return orderAmount;
    };

    private readonly _calcExclusiveLoyaltyTotal = (order: IOrder, getLoyaltyAmount: (oItem: IOrderItem) => number): number => {
        let orderAmount = 0;
        for (const oItem of order.Items) {
            orderAmount = Util.Float2(orderAmount + this._calcNonDiscountedItems(oItem));
            if (oItem.LoyaltyDiscountsQTY > 0 && oItem.NonLoyaltyDiscountsQTY === 0) {
                const itemPrice = Util.Float2(oItem.Price * oItem.LoyaltyDiscountsQTY);
                orderAmount = Util.Float2(orderAmount + Util.Float2(itemPrice - getLoyaltyAmount(oItem)));
            }
        }
        return orderAmount;
    }

    private readonly _calcDiscountedQTYs = (order: IOrder): void => {
        //Calculate total discounted and total discounted loyalty for each item to help with calculation methods 2-4
        for (const oItem of order.Items) {
            let loyaltyDiscountsQty = 0;
            let nonLoyaltyDiscountsQTY = 0;
            const iCoupons = order.ItemsCoupons.filter(x => x.ItemKey == oItem.ItemKey && x.HalfIndex == oItem.HalfIndex);
            for (const iCpn of iCoupons) {
                const appliedCoupon = order.Coupons.find(x => x.CouponKey == iCpn.CouponKey);
                if (appliedCoupon?.RewardId == null) {
                    nonLoyaltyDiscountsQTY += 1;
                } else {
                    loyaltyDiscountsQty += 1;
                }
            }
            oItem.LoyaltyDiscountsQTY = loyaltyDiscountsQty;
            oItem.NonLoyaltyDiscountsQTY = nonLoyaltyDiscountsQTY;
        }
    }

    private readonly _calcNonDiscountedItems = (oItem: IOrderItem): number => {
        let orderAmount = 0;
        const nonDiscountedQTY = oItem.Quantity - oItem.NonLoyaltyDiscountsQTY - oItem.LoyaltyDiscountsQTY;
        if (nonDiscountedQTY > 0) {
            orderAmount = Util.Float2(orderAmount + Util.Float2(oItem.Price * nonDiscountedQTY));
        }
        return orderAmount;
    }

    private readonly _checkCouponLimit = (order: IOrder, coupon: IDataCoupon, autoApplying?: boolean): boolean => {
        const limit = coupon.LimitsPerOrder;
        if (limit <= 0 || limit === CouponLimitPerOrder.OVERRIDE_207)
            return true;

        const count = order.Coupons.filter(c => c.CouponId === coupon.CouponId).length;
        if (count >= limit) {
            !autoApplying &&
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService
                        .names("CPNLIMIT")
                        .replace("??", limit.toString()),
                    true
                );
            return false;
        }

        return true;
    }

    private readonly _getQualifiedItemList = (order: IOrder, coupon: IDataCoupon, itemKey?: number | null): IvItem[] => {
        let qualifiedList: IvItem[] = [];

        switch (true) {
            case coupon.CouponId === CouponIdList.LOYALTY_OFFER_THIRDPARTY:
            case coupon.CouponId === CouponIdList.LOYALTY_REWARD_THIRDPARTY:
                qualifiedList = this._qualifyAvailableItems(order, coupon);
                break;

            case coupon.CouponTypeId === CouponTypeList.MANAGER_DISCOUNT:
                qualifiedList = this._qualifyAvailableItems(order, coupon);
                break;

            case coupon.CouponTypeId === CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER:
                if (coupon.IsVerificationNeeded && coupon.Items && coupon.Items.length > 0) {
                    qualifiedList = this.QualifyCouponItems(order, coupon, false, false, true);
                    if (qualifiedList)
                        qualifiedList = this._qualifyAvailableItems(order, coupon);
                } else {
                    qualifiedList = this._qualifyAvailableItems(order, coupon);
                }
                break;

            case coupon.CouponTypeId === CouponTypeList.PERCENTAGE_OFF_AN_ITEM_IN_CATEGORY_OR_BOGO_IN_CATEGORY:
                qualifiedList = this._qualifyAnItemInCategory(order, coupon);
                break;

            case coupon.CouponTypeId === CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY:
                qualifiedList = this._qualifyAllItemsInCategory(order, coupon);
                break;

            //case coupon.CouponTypeId === CouponTypeList.PERCENTAGE_OFF_TWO_ITEMS_IN_CATEGORY:
            //    qualifiedList = QualifyTwoItemsInCategory(coupon);
            //    break;
            case coupon.CouponTypeId === CouponTypeList.MIX_AND_MATCH_PERCENTAGE:
            case coupon.CouponTypeId === CouponTypeList.MIX_AND_MATCH_PRICE:
                qualifiedList = this.QualifyCouponItems(order, coupon, false, false, true);
                if (qualifiedList.length > 0) {
                    let allOthersQualifiedItems = this.QualifyCouponItems(order, coupon, false, true, false);
                    while (allOthersQualifiedItems.length > 0) {
                        qualifiedList.push(...allOthersQualifiedItems);
                        allOthersQualifiedItems = this.QualifyCouponItems(order, coupon, false, true, false);
                    }

                    if (coupon.MinimumItemQuantity && qualifiedList.length < coupon.MinimumItemQuantity) {
                        qualifiedList = [];
                        this._clearMarkedItems(order);
                    }
                }
                break;

            case coupon.Items && coupon.Items?.length > 0:
                qualifiedList = this.QualifyCouponItems(order, coupon, false, false, true);
                break;

            case !coupon.IsVerificationNeeded:
                qualifiedList = this._qualifyAvailableItems(order, coupon);
                if (itemKey != null)
                    qualifiedList = qualifiedList.filter(x => x.ItemKey === itemKey);
                break;
        }

        return qualifiedList;
    }

    private _qualifyLoyaltyItems = (order: IOrder): IvItem[] => {
        let qualifiedGroupList: IvItem[] = [];

        for (const [index, oItem] of order.Items.entries()) {
            const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
            if (mItem?.IsLoyalty) {
                oItem.DiscountedMarkedQuantity = oItem.Quantity;

                for (let j = 0; j < oItem.Quantity; j++) {
                    const vItem = {
                        ItemArrayIndex: index,
                        QTYIndex: j + 1,
                        cPrice: 0.00,
                        oPrice: (oItem.HalfCount > 1) ? oItem.HalfHalfPrice / oItem.HalfCount : oItem.Price,
                        ItemKey: oItem.ItemKey,
                        HalfIndex: oItem.HalfIndex,
                        ItemRecID: oItem.ItemRecId,
                        AddOn: 0.00,
                        BeforeTaxDiscount: 0.0,
                        AfterTaxDiscount: 0.0
                    };
                    qualifiedGroupList.push(vItem);
                }
            }
        }
        return qualifiedGroupList;
    }

    /**
     * Units on an order line that are closed to `coupon`.
     *
     * Bugs 85869/85870: "Allow other coupons" is a two-sided rule, but qualification only ever read
     * the incoming coupon's own flag. Units a coupon that disallows other coupons already took are
     * closed to every later coupon -- those are the only coupons that consume units
     * (_useMarkedItems), so DiscountedQuantity counts exactly them -- and a coupon that itself
     * disallows other coupons cannot land on a unit ANY other coupon already discounted.
     *
     * The register/third-party-loyalty discounts (the CouponIdList ids) are operator overrides
     * rather than configured stacking rules, so they keep their legacy reach: here that means every
     * unit, since the only path they take marked the whole line and vetoed order-wide instead. POS's
     * GetCouponBlockedQTY returns DiscountedQTY for the exclusive ones because ITS legacy path did
     * -- don't "align" the two, each preserves its own engine. Loyalty reward rows never block
     * either; ExcludeLoyaltyUsage governs those.
     *
     * Bug 86811: between a loyalty reward and a store coupon the pre-85870 rule is kept -- only a
     * unit whose earlier coupon disallows others is closed, and only to a later one that disallows
     * others too. So an incoming reward that allows others takes any unit, and a sharing store
     * coupon ignores units a reward consumed. Store coupon against store coupon stays two-sided.
     */
    private readonly _getCouponBlockedQuantity = (order: IOrder, oItem: IOrderItem, coupon: IDataCoupon): number => {
        if (this._isRegisterDiscount(coupon))
            return 0;

        if (coupon.IsLoyaltyReward)
            return coupon.AllowOtherCoupons ? 0 : oItem.DiscountedQuantity;

        if (coupon.AllowOtherCoupons)
            return Math.max(0, oItem.DiscountedQuantity - this._countRewardConsumedQuantity(order, oItem));

        return Math.max(oItem.DiscountedQuantity, this._countItemAppliedCoupons(order, oItem));
    }

    /** The register / third-party-loyalty discounts — operator overrides rather than store coupons. */
    private readonly _isRegisterDiscount = (coupon: IDataCoupon): boolean => {
        return (Object.values(CouponIdList) as number[]).includes(coupon.CouponId);
    }

    private readonly _isRewardItemCoupon = (order: IOrder, iCpn: IOrderItemCoupon): boolean => {
        return order.Coupons.some(x => x.CouponKey === iCpn.CouponKey && x.RewardId != null);
    }

    private readonly _getLineItemCoupons = (order: IOrder, oItem: IOrderItem): IOrderItemCoupon[] => {
        return order.ItemsCoupons.filter(iCpn => iCpn.ItemKey === oItem.ItemKey && iCpn.HalfIndex === oItem.HalfIndex);
    }

    /** Coupons other than loyalty rewards already on this line — one row per discounted unit. */
    private readonly _countItemAppliedCoupons = (order: IOrder, oItem: IOrderItem): number => {
        return this._getLineItemCoupons(order, oItem).filter(iCpn => !this._isRewardItemCoupon(order, iCpn)).length;
    }

    /** Units of this line that a loyalty reward consumed (its rows carry DiscountedQuantity 1). */
    private readonly _countRewardConsumedQuantity = (order: IOrder, oItem: IOrderItem): number => {
        return this._getLineItemCoupons(order, oItem)
            .filter(iCpn => this._isRewardItemCoupon(order, iCpn))
            .reduce((sum, iCpn) => sum + (iCpn.DiscountedQuantity || 0), 0);
    }

    /** Does this line still have a unit `coupon` may take, counting this pass's own marks? */
    private readonly _isItemUnitAvailable = (order: IOrder, oItem: IOrderItem, coupon: IDataCoupon): boolean => {
        return (this._getCouponBlockedQuantity(order, oItem, coupon) + oItem.DiscountedMarkedQuantity) < oItem.Quantity;
    }

    /**
     * Every unit of the order this coupon may take — the whole line minus the units already closed
     * to it (see _getCouponBlockedQuantity). Replaces the old _qualifyAllItems /
     * _qualifyAllNotDiscountedItems pair, which picked between "all units" and "units no exclusive
     * coupon took" on the incoming coupon's AllowOtherCoupons flag alone (Bugs 85869/85870).
     */
    private readonly _qualifyAvailableItems = (order: IOrder, coupon: IDataCoupon): IvItem[] => {
        // The legacy order-wide veto, kept for the register/loyalty discounts that were the only
        // coupons ever to reach it — store coupons are handled per unit above instead.
        if (this._isRegisterDiscount(coupon) && !coupon.AllowOtherCoupons && order.Coupons.length > 0 && !coupon.IsLoyaltyReward)
            return [];

        const qualifiedGroupList: IvItem[] = [];
        for (const [index, oItem] of order.Items.entries()) {
            const availableQuantity = oItem.Quantity - this._getCouponBlockedQuantity(order, oItem, coupon);
            if (availableQuantity <= 0 || (coupon.ExcludeLoyaltyUsage && oItem.IsRedeemed)) {
                continue;
            }
            oItem.DiscountedMarkedQuantity = availableQuantity;

            for (let j = 0; j < availableQuantity; j++) {
                const vItem = {
                    ItemArrayIndex: index,
                    QTYIndex: j + 1,
                    cPrice: 0.00,
                    oPrice: oItem.Price,
                    ItemKey: oItem.ItemKey,
                    HalfIndex: oItem.HalfIndex,
                    ItemRecID: oItem.ItemRecId,
                    AddOn: 0.00,
                    BeforeTaxDiscount: 0.0,
                    AfterTaxDiscount: 0.0
                };
                qualifiedGroupList.push(vItem);
            }
        }

        return qualifiedGroupList;
    }

    private _qualifyAnItemInCategory = (order: IOrder, coupon: IDataCoupon): IvItem[] => {
        const qualifiedGroupList: IvItem[] = [];

        if (coupon.IsBOGO) {
            switch (coupon.BOGOSelectionTypeID) {
                case 1: {  //Auto select - Discount the lowest priced item
                    const vItem1 = this._qualifyHighestItemInCategory(order, coupon);
                    if (vItem1) {
                        vItem1.cPrice = 0.00; // don't add coupon item price of the bogos first item
                        vItem1.oPrice = 0.00;
                        qualifiedGroupList.push(vItem1);
                        const vItem2 = this._qualifyLowestItemInCategory(order, coupon);
                        if (vItem2) {
                            qualifiedGroupList.push(vItem2);
                            return qualifiedGroupList;
                        } else {
                            const oItem = order.Items[vItem1.ItemArrayIndex];
                            if (oItem.DiscountedMarkedQuantity > 0)
                                oItem.DiscountedMarkedQuantity = 0;
                        }
                    }
                    break;
                }
                case 2: { //Auto select - Discount the second highest priced item
                    const vItem1 = this._qualifyHighestItemInCategory(order, coupon);
                    if (vItem1) {
                        vItem1.cPrice = 0.00; // don't add coupon item price of the bogos first item
                        vItem1.oPrice = 0.00;
                        qualifiedGroupList.push(vItem1);
                        const vItem2 = this._qualifyHighestItemInCategory(order, coupon);
                        if (vItem2) {
                            qualifiedGroupList.push(vItem2);
                            return qualifiedGroupList;
                        } else {
                            const oItem = order.Items[vItem1.ItemArrayIndex];
                            if (oItem.DiscountedMarkedQuantity > 0)
                                oItem.DiscountedMarkedQuantity = 0;
                        }
                    }
                    break;
                }
                case 3: { //Auto select - Discount the highest priced item
                    const vItem1 = this._qualifyHighestItemInCategory(order, coupon);
                    if (vItem1) {
                        qualifiedGroupList.push(vItem1);
                        const vItem2 = this._qualifyHighestItemInCategory(order, coupon);
                        if (vItem2) {
                            vItem2.cPrice = 0.00;
                            vItem2.oPrice = 0.00;
                            qualifiedGroupList.push(vItem2);
                            return qualifiedGroupList;
                        } else {
                            const oItem = order.Items[vItem1.ItemArrayIndex];
                            if (oItem.DiscountedMarkedQuantity > 0)
                                oItem.DiscountedMarkedQuantity = 0;
                        }
                    }
                    break;
                }
            }
        } else {
            for (const cpnItmCat of coupon.ItemCategories) {
                for (const [index, oItem] of order.Items.entries()) {
                    if (!oItem.IsRemoved && this._isItemUnitAvailable(order, oItem, coupon)) {
                        const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId) || null;

                        if (mItem?.ItemCategoryId !== cpnItmCat.CategoryId)
                            continue;

                        if (coupon.ExcludeLoyaltyUsage && oItem.IsRedeemed)
                            continue;

                        if (oItem.HalfCount > 1)
                            this._markAllHalves(order, oItem.ItemKey);
                        else
                            oItem.DiscountedMarkedQuantity += 1;

                        const vItem = {
                            ItemArrayIndex: index,
                            QTYIndex: oItem.DiscountedQuantity + oItem.DiscountedMarkedQuantity + 1,
                            cPrice: Util.Float2(oItem.Price * coupon.Discount / 100),
                            oPrice: (oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price,
                            ItemKey: oItem.ItemKey,
                            HalfIndex: oItem.HalfIndex,
                            ItemRecID: oItem.ItemRecId,
                            AddOn: 0.00,
                            BeforeTaxDiscount: 0.0,
                            AfterTaxDiscount: 0.0
                        };
                        qualifiedGroupList.push(vItem);

                        if (qualifiedGroupList.length > 0) {
                            break;
                        }
                    }
                }
            }
        }
        return qualifiedGroupList;
    }

    private _qualifyHighestItemInCategory = (order: IOrder, coupon: IDataCoupon): IvItem | null => {
        let maxPrice = 0.0;
        let itemIndex = -1;
        for (const cat of coupon.ItemCategories) {
            for (const [index, oItem] of order.Items.entries()) {
                if (!this._isItemUnitAvailable(order, oItem, coupon))
                    continue;

                if (coupon.ExcludeLoyaltyUsage && oItem.IsRedeemed) {
                    continue;
                }

                const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
                if (mItem?.ItemCategoryId !== cat.CategoryId)
                    continue;

                const oPrice = (oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price;
                if (oPrice > maxPrice) {
                    maxPrice = oPrice;
                    itemIndex = index;
                }
            }
        }

        if (itemIndex > -1) {
            const oItem = order.Items[itemIndex];
            if (oItem.HalfCount > 1)
                this._markAllHalves(order, oItem.ItemKey);
            else
                oItem.DiscountedMarkedQuantity += 1;

            const vItem = {
                ItemArrayIndex: itemIndex,
                QTYIndex: oItem.DiscountedQuantity + oItem.DiscountedMarkedQuantity + 1,
                oPrice: (oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price,
                cPrice: 0.00,
                ItemKey: oItem.ItemKey,
                HalfIndex: oItem.HalfIndex,
                ItemRecID: oItem.ItemRecId,
                AddOn: 0.00,
                BeforeTaxDiscount: 0.0,
                AfterTaxDiscount: 0.0
            };
            return vItem;
        }
        return null;
    }

    private _qualifyLowestItemInCategory = (order: IOrder, coupon: IDataCoupon): IvItem | null => {
        let minPrice = 1000000.0;
        let itemIndex = -1;
        for (const cat of coupon.ItemCategories) {
            for (const [index, oItem] of order.Items.entries()) {
                if (!this._isItemUnitAvailable(order, oItem, coupon))
                    continue;

                if (coupon.ExcludeLoyaltyUsage && oItem.IsRedeemed) {
                    continue;
                }

                const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
                if (mItem?.ItemCategoryId !== cat.CategoryId)
                    continue;

                let oPrice = 0.0;
                if (oItem.HalfCount > 1)
                    oPrice = oItem.HalfHalfPrice;
                else
                    oPrice = oItem.Price;

                if (oPrice < minPrice) {
                    minPrice = oPrice;
                    itemIndex = index;
                }
            }
        }
        if (itemIndex > -1) {
            let oItem = order.Items[itemIndex];
            if (oItem.HalfCount > 1)
                this._markAllHalves(order, oItem.ItemKey);
            else
                oItem.DiscountedQuantity += 1;

            const vItem = {
                ItemArrayIndex: itemIndex,
                QTYIndex: oItem.DiscountedQuantity + oItem.DiscountedMarkedQuantity + 1,
                cPrice: 0.00,
                oPrice: (oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price,
                ItemKey: oItem.ItemKey,
                HalfIndex: oItem.HalfIndex,
                ItemRecID: oItem.ItemRecId,
                AddOn: 0.00,
                BeforeTaxDiscount: 0.0,
                AfterTaxDiscount: 0.0
            };
            return vItem;
        }
        return null;
    }

    private _qualifyAllItemsInCategory = (order: IOrder, coupon: IDataCoupon): IvItem[] => {
        const qualifiedGroupList: IvItem[] = [];

        for (const cat of coupon.ItemCategories) {
            for (const [index, oItem] of order.Items.entries()) {
                if (!this._isItemUnitAvailable(order, oItem, coupon) || oItem.IsRedeemed)
                    continue;

                const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
                if (mItem?.ItemCategoryId !== cat.CategoryId)
                    continue;

                const availableQuantity = oItem.Quantity - this._getCouponBlockedQuantity(order, oItem, coupon) - oItem.DiscountedMarkedQuantity;
                for (let k = 0; k < availableQuantity; k++) {
                    if (oItem.HalfCount > 1)
                        this._markAllHalves(order, oItem.ItemKey);
                    else
                        oItem.DiscountedMarkedQuantity += 1;

                    const vItem = {
                        ItemArrayIndex: index,
                        QTYIndex: oItem.DiscountedQuantity + oItem.DiscountedMarkedQuantity + 1,
                        cPrice: 0.00,
                        oPrice: (oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price,
                        ItemKey: oItem.ItemKey,
                        HalfIndex: oItem.HalfIndex,
                        ItemRecID: oItem.ItemRecId,
                        AddOn: 0.00,
                        BeforeTaxDiscount: 0.0,
                        AfterTaxDiscount: 0.0
                    };
                    qualifiedGroupList.push(vItem);
                }
            }
        }
        return qualifiedGroupList;
    }

    public QualifyCouponItems = (order: IOrder, coupon: IDataCoupon, autoApplying: boolean, qualifyAnyQTY: boolean, clearMarkedQTY: boolean, returnItems: boolean = false): IvItem[] => {
        const BOGOType = {
            Lowest: 1,
            Second_Highest: 2,
            Highest: 3
        };

        let QualifyingGroup = -1;
        let qualifiedGroupList: IvItem[] = [];
        let groupCount = 0;
        let vItem: IvItem | null = null;
        let vItemList: IvItem[] | null = null;

        const couponGroups = Array.from(new Set(coupon.Items.map(x => x.GroupIndex)));
        for (const group of couponGroups) {
            groupCount += 1;
            QualifyingGroup = group;
            if (coupon.IsBOGO && groupCount === 1) {
                switch (coupon.BOGOSelectionTypeID) {
                    case BOGOType.Lowest:  //Auto select - Discount the lowest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Highest);
                        break;
                    case BOGOType.Second_Highest: //Auto select - Discount the second highest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Highest);
                        break;
                    case BOGOType.Highest: //Auto select - Discount the highest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Highest);
                        break;
                }
                if (vItem) {
                    if (vItem.HalfIndex > 0) {
                        const qualifiedHalves = this._qualifyAllHalfs(order, coupon, vItem, true);
                        qualifiedGroupList = qualifiedGroupList.concat(qualifiedHalves);
                    } else {
                        vItem.cPrice = 0.00; // don't add coupon item price of the bogos first item
                        vItem.oPrice = 0.00;
                        qualifiedGroupList.push(vItem);
                    } 
                }
            } else if (coupon.IsBOGO && groupCount === 2) {
                switch (coupon.BOGOSelectionTypeID) {
                    case BOGOType.Lowest:  //Auto select - Discount the lowest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Lowest);
                        break;
                    case BOGOType.Second_Highest: //Auto select - Discount the second highest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Second_Highest);
                        break;
                    case BOGOType.Highest: //Auto select - Discount the highest priced item
                        vItem = this._qualifyCouponItemsGroupByBOGOType(order, coupon, QualifyingGroup, BOGOType.Highest);
                        break;
                }
                if (vItem) {
                    if (vItem.HalfIndex > 0) {
                        const qualifiedHalves = this._qualifyAllHalfs(order, coupon, vItem);
                        qualifiedGroupList = qualifiedGroupList.concat(qualifiedHalves);
                    } else
                        qualifiedGroupList.push(vItem);
                    vItemList = qualifiedGroupList;
                }
            } else {
                vItemList = this._qualifyCouponItemsGroupQTY(order, coupon, QualifyingGroup, autoApplying, qualifyAnyQTY);
                if (vItemList) {
                    for (const item of vItemList) {
                        vItem = item;

                        if (item.HalfIndex > 0)
                            qualifiedGroupList = qualifiedGroupList.concat(this._qualifyAllHalfs(order, coupon, item));
                        else
                            qualifiedGroupList.push(item);
                    }
                } else
                    vItem = null;
            }

            if (!vItem && !returnItems)
                break;
        }

        // if last group has qualified or there was no coupon items to qualified return the list otherwise claer marked items and return nothing;
        if (vItemList || QualifyingGroup === -1) {
            if (returnItems)
                order.Items.filter(x => x.DiscountedMarkedQuantity > 0).forEach(x => x.DiscountedMarkedQuantity = 0);
            return qualifiedGroupList;
        } else {
            if (clearMarkedQTY) {
                for (const oItem of order.Items) {
                    if (oItem.DiscountedMarkedQuantity > 0)
                        oItem.DiscountedMarkedQuantity = 0;
                }
            }
            if (returnItems)
                return qualifiedGroupList;
            return [];
        }
    }

    private _qualifyCouponItemsGroupByBOGOType = (order: IOrder, coupon: IDataCoupon, group: number, priceType: number): IvItem | null => {
        const oItemIndexes: IQualifyItemExt[] = [];
        let finalItem: IQualifyItemExt = {
            Index: -1,
            QTYIndex: -1,
            CouponItemIndex: -1
        };

        const groupItems = coupon.Items.filter(cpn => cpn.GroupIndex == group);
        // important don't change this to forEach
        for (let i = 0; i < groupItems.length; i++) {
            const cItem = groupItems[i];
            const qItem = this._findCouponItem(order, coupon, cItem.ItemId, cItem.SizeId, cItem.ModifierCount);
            if (qItem.Index > -1) {
                qItem.CouponItemIndex = i;
                oItemIndexes.push(qItem);
                i--; // important this make to look for same item again
            }
        }

        if (oItemIndexes.length > 0) {
            let iIndex = -1;
            let finalPrice = (priceType == 1) ? Number.MAX_VALUE : 0.00;
            let highestIndex: IQualifyItemExt | null = null;

            if (priceType == 2)
                highestIndex = oItemIndexes.find(x => order.Items[x.Index].Price == Math.max(...oItemIndexes.map(x => order.Items[x.Index].Price))) || null;

            for (const [index, oItemIndex] of oItemIndexes.entries()) {
                const item = order.Items[oItemIndex.Index];
                const iPrice = item.HalfCount > 1 ? item.HalfHalfPrice : item.Price;

                if (priceType == 1 && iPrice < finalPrice) {
                    finalPrice = iPrice;
                    finalItem = oItemIndex;
                    iIndex = index;
                } else if (priceType == 2) {
                    if (oItemIndexes.length > 1 && highestIndex && (oItemIndex.Index != highestIndex.Index || oItemIndex.QTYIndex != highestIndex.QTYIndex) && iPrice > finalPrice) {
                        finalPrice = iPrice;
                        finalItem = oItemIndex;
                        iIndex = index;
                    } else if (oItemIndexes.length - index == 1) {
                        finalItem = oItemIndex;
                        iIndex = index;
                    }
                } else if (priceType == 3 && iPrice > finalPrice) {
                    finalPrice = iPrice;
                    finalItem = oItemIndex;
                    iIndex = index;
                }
            }

            for (const [index, oItemIndex] of oItemIndexes.entries()) {
                if (index != iIndex) {
                    const item = order.Items[oItemIndex.Index];
                    if (item.HalfCount > 1)
                        this._unMarkAllHalves(order, item.ItemKey);
                    else
                        item.DiscountedMarkedQuantity -= 1;
                }
            }
        }

        if (finalItem.Index > -1) {
            const cItem = groupItems[Number(finalItem.CouponItemIndex)];
            const vItem = this._getQualifiedCouponItem(order, finalItem, cItem.ItemId, cItem.SizeId, cItem.ModifierCount, cItem.AddOnPrice, coupon.ExcludeLoyaltyUsage);
            if (vItem)
                vItem.CouponItemsGroupID = group;
            return vItem;
        }
        return null;
    }

    private _getQualifiedCouponItem = (order: IOrder, qItem: IQualifyItem, cItemID: number, cSizeID: number, cModifierCount: number, cAddOnPrice: number, cExcludeLoyaltyUsage: boolean): IvItem | null => {
        if (qItem.Index <= -1)
            return null;

        const oItem = order.Items[qItem.Index];

        if (cExcludeLoyaltyUsage && oItem.IsRedeemed) {
            return null;
        }

        const vItem = {
            ItemArrayIndex: qItem.Index,
            QTYIndex: qItem.QTYIndex,
            cPrice: this._priceCouponItem(cItemID, cSizeID, cModifierCount),
            oPrice: oItem.Price, //(oItem.HalfCount > 1) ? oItem.HalfHalfPrice : oItem.Price, ;BUG #36324 (Commented old code)
            ItemKey: oItem.ItemKey,
            HalfIndex: oItem.HalfIndex,
            HalfCount: oItem.HalfCount,
            ItemRecID: oItem.ItemRecId,
            AddOn: cAddOnPrice,
            BeforeTaxDiscount: 0.0,
            AfterTaxDiscount: 0.0,
            CouponItemsGroupID: null
        };
        return vItem;
    }

    private _priceCouponItem = (itemID: number, sizeID: number, modifierCount: number): number => {
        const mItem = this._getItem(itemID, this._data.getProperty("Items"));
        if (!mItem)
            return 0.00;

        const matchingPrice = mItem.Prices.find(price => price.SizeId === sizeID);
        if (!matchingPrice)
            return 0.00;

        let price = Util.Float2(matchingPrice.Price) + (Util.Float2(matchingPrice.PerModifierPrice) * modifierCount);
        price = Math.max(price, Util.Float2(matchingPrice.MinimumPrice));
        return price;
    }

    private _getItem(itemID: number, items: IDataItem[]): IDataItem | null {
        for (const [index, item] of items.entries()) {
            if (item.ItemId === itemID) {
                item.Index = index;
                return item;
            }
        }
        return null;
    }

    private _checkSameCouponAppliedQtyTimes = (order: IOrder, oItem: IOrderItem, coupon: IDataCoupon): boolean => {
        const itemCoupons = order.ItemsCoupons.filter(iCop => iCop.ItemKey == oItem.ItemKey && iCop.HalfIndex == oItem.HalfIndex && iCop.ItemRecId == oItem.ItemRecId);
        let count = 0;
        itemCoupons.forEach((iCpn) => {
            if (order.Coupons.find(x => x.CouponKey == iCpn.CouponKey && x.CouponId == coupon.CouponId)) {
                count++;
            }
        });
        return (count >= oItem.Quantity);
    }

    private _updateQualifyingItem = (order: IOrder, qItem: IQualifyItemExt, oItem: IOrderItem, i: number): IQualifyItemExt => {
        const updatedQItem: IQualifyItemExt = {
            Index: i,
            QTYIndex: oItem.DiscountedQuantity + oItem.DiscountedMarkedQuantity + 1,
            CouponItemIndex: qItem.CouponItemIndex,
            CouponItemID: oItem.ItemId
        };
        if (oItem.HalfCount > 1) {
            this._markAllHalves(order, oItem.ItemKey);
        } else {
            oItem.DiscountedMarkedQuantity += 1;  // Notice: This modifies the passed oItem directly.
        }
        return updatedQItem;
    };

    private _checkCouponAppliedToItem = (order: IOrder, oItem: IOrderItem, applyingCouponKeys: number[]): boolean => {
        return order.ItemsCoupons.some(x => x.ItemKey == oItem.ItemKey && applyingCouponKeys.includes(x.CouponKey));
    }

    private _findCouponItem = (order: IOrder, coupon: IDataCoupon, itemID: number, sizeID: number, modifierCount: number): IQualifyItemExt => {
        let qItem: IQualifyItemExt = {
            Index: -1,
            QTYIndex: -1,
            CouponItemIndex: -1
        };

        let items = order.Items.filter((x: IOrderItem) => x.ItemId == itemID
            && x.SizeId == sizeID
            && !x.IsRemoved
            && this._isItemUnitAvailable(order, x, coupon)
            && !this._checkSameCouponAppliedQtyTimes(order, x, coupon)
            && this._getItemPricedModifierCount(x) == modifierCount);

        if (items.length == 0) {
            items = order.Items.filter((x: IOrderItem) => x.ItemId == itemID
                && x.SizeId == sizeID
                && !x.IsRemoved
                && this._isItemUnitAvailable(order, x, coupon)
                && !this._checkSameCouponAppliedQtyTimes(order, x, coupon)
                && this._getItemPricedModifierCount(x) > modifierCount);
        }

        for (const [index, oItem] of order.Items.entries()) {
            if (!items.some((x: IOrderItem) => x.ItemKey === oItem.ItemKey))
                continue;

            if ((!coupon.ExactMatch || (coupon.ExactMatch && this._getItemPricedModifierCount(oItem) == modifierCount))
                && (!coupon.AllowOtherCoupons || (coupon.AllowOtherCoupons && !this._checkSameCouponAppliedQtyTimes(order, oItem, coupon)))
            ) {
                qItem = this._updateQualifyingItem(order, qItem, oItem, Number(index));
                break;
            }
        }
        return qItem;
    }

    private _getItemPricedModifierCount = (oItem: IOrderItem): number => {
        let mCount = 0;
        for (const mod of oItem.Modifiers) {
            const preMod = OnlineOrderingUtil.GetPreMod(mod.PreModifierId, this._data.getProperty("PreModifiers"));
            let preModPriceMultiplier = 1.00;
            if (preMod)
                preModPriceMultiplier = Number(preMod.PriceCountMultiplier);


            if (mod.CountForPricing || mod.CountForPricingByItself)
                mCount += (1 * preModPriceMultiplier);
        }
        return mCount;
    }

    private readonly _qualifyAllHalfs = (order: IOrder, coupon: IDataCoupon, vItem: IvItem, ignoreBOGOItemPrice = false): IvItem[] => {
        const vItems: IvItem[] = [];
        const halfCount = vItem.HalfCount || 1;

        // ---- Split price & add-on across halves, track rounding remainder on the "base" half ----
        let perHalfCPrice = Util.Float2(vItem.cPrice / halfCount);
        const perHalfAddOn = Util.Float2(vItem.AddOn / halfCount);

        let priceRemainder = Util.Float2(vItem.cPrice - perHalfCPrice * halfCount);
        const addOnRemainder = Util.Float2(vItem.AddOn - perHalfAddOn * halfCount);

        // Update the original vItem (the "base" half)
        vItem.cPrice = ignoreBOGOItemPrice ? 0.0 : Util.Float2(perHalfCPrice + priceRemainder);
        vItem.oPrice = ignoreBOGOItemPrice ? 0.0 : vItem.oPrice;
        vItem.AddOn = Util.Float2(perHalfAddOn + addOnRemainder);

        // The un-covered priced-modifier charge is a whole-item amount (it depends only on the coupon and
        // the qualifying half), so compute it once here and split it across the base half and its siblings.
        const overageTotal = this._getPricedModifierOverageTotal(order, coupon, vItem);
        const modifierOverage = this._splitChargeBackAcrossHalves(overageTotal, halfCount);

        // Grab the DiscountedMarkedQuantity from the matching half. For a fixed-price coupon, also rebase
        // the qualified (base) half onto its own order price so the fixed price applies to the item's
        // actual price in every half-and-half pricing mode, matching how the sibling halves are priced below.
        const match = order.Items.find(oItem =>
            oItem.ItemKey === vItem.ItemKey &&
            oItem.HalfIndex === vItem.HalfIndex
        );

        const discountedMarkedQTY = match?.DiscountedMarkedQuantity ?? 0;

        if (match && coupon.CouponTypeId == CouponTypeList.FIXED_PRICE && !ignoreBOGOItemPrice) {
            // Price the half at its own order price (the halves already sum to the item total, so
            // there's no remainder to redistribute) less its share of any un-covered modifier charges.
            perHalfCPrice = Util.Float2(match.Price / halfCount);
            priceRemainder = 0;
            vItem.cPrice = Util.Float2(match.Price - modifierOverage.base);
        }

        vItems.push(vItem);

        // ---- Build sibling halves ----
        order.Items.forEach((oItem, index) => {
            const isSameItem = oItem.ItemKey === vItem.ItemKey;
            const isSameHalf = oItem.HalfIndex === vItem.HalfIndex;

            if (!isSameItem || isSameHalf) {
                return;
            }

            const halfCPrice = this._getFixedPriceCouponPriceForHalf(order, coupon, vItem, perHalfCPrice, oItem.Price, priceRemainder, modifierOverage.sibling);

            // Sync discounted quantity
            oItem.DiscountedMarkedQuantity = discountedMarkedQTY;

            const halfItem: IvItem = {
                ItemArrayIndex: index,
                QTYIndex: vItem.QTYIndex,
                cPrice: ignoreBOGOItemPrice ? 0.0 : halfCPrice,
                oPrice: ignoreBOGOItemPrice ? 0.0 : oItem.Price,
                ItemKey: oItem.ItemKey,
                HalfIndex: oItem.HalfIndex,
                HalfCount: oItem.HalfCount,
                ItemRecID: oItem.ItemRecId,
                AddOn: perHalfAddOn,
                BeforeTaxDiscount: 0.0,
                AfterTaxDiscount: 0.0,
                CouponItemsGroupID: null
            };

            vItems.push(halfItem);
        });

        return vItems;
    }

    /**
     * Determines the coupon price (cPrice) for a single sibling half of a half-and-half item.
     *
     * The vItem passed in is the first qualified half of the item; its modifier pricing details
     * (coupon item match, modifier count, etc.) carry over to the other halves being priced here.
     * This keeps every half of the same item consistent with the modifier pricing established by
     * the qualifying half.
     *
     * @param order             The current order.
     * @param coupon            The coupon being applied.
     * @param vItem             The first qualified half. Its ItemArrayIndex/CouponItemsGroupID are
     *                          used to look up the underlying order item and the matching coupon item.
     * @param vItemHalfCPrice   The per-half coupon price already computed for the base half. Used as
     *                          the fallback/default when this isn't a fixed-price coupon scenario.
     * @param oItemOrderPrice   The current (menu) price of the sibling half being priced.
     * @param priceRemainder    Rounding remainder from splitting the price across halves; absorbed here
     *                          so the halves sum back to the original total.
     * @param modifierOverage   This sibling half's share of the priced-modifier charge the coupon doesn't
     *                          cover (precomputed once by the caller; every sibling half carries the same
     *                          share, while the base half absorbs any leftover cent — see
     *                          _splitChargeBackAcrossHalves).
     * @returns                 The coupon price to assign to this sibling half.
     */
    private readonly _getFixedPriceCouponPriceForHalf = (order: IOrder, coupon: IDataCoupon, vItem: IvItem, vItemHalfCPrice: number, oItemOrderPrice: number, priceRemainder: number, modifierOverage: number): number => {
        // Only fixed-price coupons need special half pricing; otherwise keep the pre-computed per-half price.
        if (coupon.CouponTypeId != CouponTypeList.FIXED_PRICE)
            return vItemHalfCPrice;

        // Bug 84767: ExactMatch (EXCMCH) coupons are NOT exempt from the charge-back here. "Exclude modifier
        // charges" covers the toppings the coupon's own allowance pays for, and for a whole item that is the
        // entire story -- qualification already forced the item to carry exactly ModifierCount toppings, so
        // there is never an overage. A half & half item qualifies on ONE half only (see _findCouponItem),
        // so a sibling half can carry more toppings than the coupon covers. Those extra toppings were never
        // paid for by the coupon and must still be charged. When there is no overage the result is identical
        // to the plain order price, which is what this path used to short-circuit to.

        // Resolve the order item behind the qualifying half and find its matching coupon item definition.
        // The qualifying half's coupon item (cItem) is what carries the modifier pricing over to this half.
        const vItemOItem = order.Items[vItem.ItemArrayIndex];
        const vItemId = vItemOItem.ItemId;
        const cItem = coupon.Items.find(x => x.GroupIndex === vItem.CouponItemsGroupID && x.ItemId === vItemId);
        if (!cItem)
            return vItemHalfCPrice;

        // Charge for the priced modifiers beyond what the coupon covers (precomputed per half) on top of
        // the fixed order price (less the rounding remainder). When there's no overage this is 0 and the
        // half is simply priced at its own order price.
        const halfCPrice = Util.Float2(oItemOrderPrice - modifierOverage - priceRemainder);

        // No extra priced modifiers (or the computed price exceeded the order price): fall back to the
        // plain order price for this half, less the rounding remainder.
        if (modifierOverage <= 0 || halfCPrice > oItemOrderPrice)
            return Util.Float2(oItemOrderPrice - priceRemainder);

        return halfCPrice;
    }

    /**
     * Splits a whole-item charge-back across the item's halves in WHOLE CENTS.
     *
     * Bugs 84748 / 84749: an even division can land on a fraction of a cent -- a $2.25 topping charge over 2
     * halves is $1.125 -- and rounding that to $1.13 on EVERY half bills the customer $2.26 for a $2.25
     * topping. The qualifying ("base") half absorbs the leftover cent(s) instead, exactly like the price and
     * add-on remainders in _qualifyAllHalfs, so the halves always sum back to the whole-item charge. The old
     * Float2(total / halfCount) predates these bugs; charging the FULL per-modifier price under Highest Price
     * is what made an odd-cent split reachable.
     *
     * @param total      The whole-item charge-back to distribute. Never negative.
     * @param halfCount  Number of halves the item is split into.
     * @returns          The amount to charge the base half and the amount to charge each sibling half.
     */
    private readonly _splitChargeBackAcrossHalves = (total: number, halfCount: number): { base: number, sibling: number } => {
        const amount = Util.Float2(total);
        if (halfCount <= 1)
            return { base: amount, sibling: amount };

        const totalCents = Math.round(amount * 100);
        const siblingCents = Math.floor(totalCents / halfCount);

        return {
            base: Util.Float2((totalCents - siblingCents * (halfCount - 1)) / 100),
            sibling: Util.Float2(siblingCents / 100)
        };
    }

    /**
     * Computes the whole-item priced-modifier charges the coupon does NOT cover.
     *
     * A fixed-price coupon covers up to cItem.ModifierCount priced modifiers (or the item's own free-modifier
     * allowance, whichever is larger); anything beyond that is charged on top of the fixed price. The caller
     * splits this whole-item amount across the halves via _splitChargeBackAcrossHalves. Returns 0 whenever
     * there is nothing extra to charge (non-fixed coupon, no matching coupon item, or the allowance isn't
     * exceeded).
     *
     * Bug 84767: this applies to ExactMatch (EXCMCH) coupons too. Only ONE half has to match the coupon's
     * topping count for the item to qualify, so the sibling half's extra toppings are a genuine overage
     * and are charged back like any other.
     *
     * @param order      The current order.
     * @param coupon     The coupon being applied.
     * @param vItem      The qualifying half; its ItemArrayIndex/CouponItemsGroupID resolve the order
     *                   item and matching coupon item that establish the covered-modifier allowance.
     * @returns          The whole-item priced-modifier overage to deduct from the item's order price.
     */
    private readonly _getPricedModifierOverageTotal = (order: IOrder, coupon: IDataCoupon, vItem: IvItem): number => {
        if (coupon.CouponTypeId != CouponTypeList.FIXED_PRICE)
            return 0;

        // Get the highest possible modifer count 
        const vItemOItem = order.Items[vItem.ItemArrayIndex];
        const cItem = coupon.Items
            .filter(x => x.GroupIndex === vItem.CouponItemsGroupID && x.ItemId === vItemOItem.ItemId)
            .reduce<typeof coupon.Items[number] | null>((max, curr) => (!max || curr.ModifierCount > max.ModifierCount ? curr : max), null);
        if (!cItem)
            return 0;

        // The charge for the priced modifiers neither the coupon nor the item's own free-modifier allowance
        // covers, taken straight off the item's menu price so it always matches what the customer sees.
        const pricedModifierOverage = this._getPricedModifierChargeBack(order, vItem.ItemKey, cItem.ModifierCount);

        // Bug 82910: a fixed-price coupon must NOT discount fixed upcharges (e.g. an upcharged crust). The
        // whole-item path preserves them because the coupon's regular price never includes the upcharge; the
        // half path rebases each half onto its order price (which does include it), so the upcharge would be
        // discounted away. Add the item's total AddsToPrice upcharge back on top of the fixed price. Upcharges
        // are not counted toward the covered-topping allowance.
        const upchargeTotal = this._getItemUpchargeTotal(order, vItem.ItemKey);

        // The whole-item charge-back: priced-modifier overage + fixed upcharges.
        return Util.Float2(pricedModifierOverage + upchargeTotal);
    }

    /**
     * Resolves an item's price row for a size, matching how the rest of the coupon code looks a price up.
     * Returns null when the menu data, the item, or a price row for that size is missing.
     */
    private readonly _getItemPriceRow = (itemID: number, sizeID: number): IItemPrice | null => {
        const items = this._data.getProperty("Items");
        if (!items)
            return null;

        const mItem = this._getItem(itemID, items);
        if (!mItem?.Prices)
            return null;

        return mItem.Prices.find(price => price.SizeId === sizeID) ?? null;
    }

    /**
     * Bug 82915: the number of "free" priced modifiers an item's price setup grants (the Back Office "Free
     * Modifiers" allowance). This is not a discrete field -- it is encoded as the gap between the item's base
     * Price and its MinimumPrice floor: freeModCount = round((MinimumPrice - Price) / PerModifierPrice).
     * Modifiers within this count are absorbed by the MinimumPrice floor at regular pricing time and never
     * raise the item price, so a fixed-price coupon must not charge them back. Returns 0 when there is no
     * MinimumPrice floor (no allowance possible, and it also avoids an undefined -> NaN in the division) or
     * no per-modifier price to divide by.
     */
    private readonly _getItemFreeModifierCount = (iPrice: IItemPrice | null): number => {
        if (!iPrice?.MinimumPrice)
            return 0;

        const perModifierPrice = Util.Float2(iPrice.PerModifierPrice);
        if (perModifierPrice <= 0)
            return 0;

        return Math.max(0, Math.round((Util.Float2(iPrice.MinimumPrice) - Util.Float2(iPrice.Price)) / perModifierPrice));
    }

    /**
     * Prices a single half of a half-and-half item at a given priced-modifier count, mirroring regular pricing
     * (OnlineOrderingUtil._buildFinalPrice): every half is priced as a WHOLE pizza -- base price plus the
     * per-modifier charge -- then clamped UP to the MinimumPrice floor and DOWN to the MaximumPrice cap. The
     * MinimumPrice floor is what makes the item's "Free Modifiers" allowance free; the MaximumPrice cap is
     * what makes toppings past a price ceiling free.
     *
     * AddsToPrice upcharges are deliberately left out: regular pricing adds them to both the running price and
     * the floor, so they cancel out of the modifier delta this feeds, and a fixed-price coupon preserves them
     * separately via _getItemUpchargeTotal (Bug 82910).
     */
    private readonly _getHalfMenuPrice = (iPrice: IItemPrice | null, modifierCount: number): number => {
        if (!iPrice)
            return 0;

        let price = Util.Float2(Util.Float2(iPrice.Price) + modifierCount * Util.Float2(iPrice.PerModifierPrice));

        const minPrice = Util.Float2(iPrice.MinimumPrice);
        if (minPrice && price < minPrice)
            price = minPrice;

        // Regular pricing compares against the ABSOLUTE MaximumPrice but assigns the raw (possibly negative) value.
        const maxPrice = Util.Float2(iPrice.MaximumPrice);
        if (maxPrice && price > Math.abs(maxPrice))
            price = maxPrice;

        return price;
    }

    /**
     * Bugs 84748 / 84749: the total priced-modifier charge a fixed-price coupon must add back on top of the
     * fixed price -- the exact amount the item's MENU price rises because of priced modifiers that neither the
     * coupon nor the item's own free-modifier allowance covers.
     *
     * This is derived from the menu price rather than from a modifier count because the two do not scale
     * together on a half-and-half item. Regular pricing (OnlineOrderingUtil.PriceItem) prices every half as a
     * whole pizza and then combines the halves according to setting 22, HHP: 1 = Highest Price takes the MAX of
     * the halves, otherwise Average Price takes their sum / HalfCount. So an extra topping on one half raises
     * the item by PerModifierPrice / HalfCount under Average but by the FULL PerModifierPrice under Highest. The
     * previous count-weighted math (sum of the per-half counts / HalfCount) only matched the Average case; under
     * Highest Price it charged back half of what the menu price actually went up by and the coupon discount
     * silently absorbed the difference, over-discounting the item.
     *
     * The exempt allowance is per half: each half is floored at MinimumPrice independently, so it receives the
     * item's full free-modifier allowance, and the coupon's covered-topping count applies to each half the same
     * way. The two pools OVERLAP rather than stack (Bug 82915) -- a topping exempt under either pool is exempt
     * -- so the larger of the two is used, never their sum.
     */
    private readonly _getPricedModifierChargeBack = (order: IOrder, itemKey: number, couponModifierCount: number): number => {
        const halves = order.Items.filter((x: IOrderItem) => x.ItemKey === itemKey);
        if (halves.length === 0)
            return 0;

        const highestPrice = this._data.getProperty("Settings").HHP === 1;    //1=Highest Price|2=Average Price

        // Price every half twice: once with all of its priced modifiers and once with only its exempt ones. The
        // gap between the two combined item prices is exactly the charge the coupon does not cover.
        let fullPrice = 0.00;
        let exemptPrice = 0.00;
        for (const oItem of halves) {
            const iPrice = this._getItemPriceRow(oItem.ItemId, oItem.SizeId);
            const exemptCount = Math.max(this._getItemFreeModifierCount(iPrice), couponModifierCount);
            const modifierCount = this._getItemPricedModifierCount(oItem);

            const halfFullPrice = this._getHalfMenuPrice(iPrice, modifierCount);
            const halfExemptPrice = this._getHalfMenuPrice(iPrice, Math.min(modifierCount, exemptCount));

            if (highestPrice) {
                fullPrice = Math.max(fullPrice, halfFullPrice);
                exemptPrice = Math.max(exemptPrice, halfExemptPrice);
            } else {
                fullPrice += halfFullPrice;
                exemptPrice += halfExemptPrice;
            }
        }

        if (!highestPrice) {
            const halfCount = halves[0].HalfCount || halves.length;
            fullPrice = Util.Float2(fullPrice / halfCount);
            exemptPrice = Util.Float2(exemptPrice / halfCount);
        }

        return Math.max(0, Util.Float2(fullPrice - exemptPrice));
    }

    /**
     * Bug 82910: Sum of the fixed "upcharge modifier" amounts (menu modifiers flagged AddsToPrice, e.g. an
     * upcharged crust) baked into an item's price. These are hard upcharges a fixed-price coupon must
     * preserve: they are NOT priced modifiers, so they are neither discounted nor counted toward the
     * coupon's covered-topping allowance. Computed the same way the item-price calculator adds them
     * (pre-modifier price-count multiplier x the modifier's size AddOnPrice, with non-chargeable display
     * groups pinned to a multiplier of 1). Whole-pizza (non-halfable) modifiers such as a crust are stored
     * on every half but priced once, so their upcharge is counted once; half-able modifiers (toppings) are
     * counted per half.
     */
    private readonly _getItemUpchargeTotal = (order: IOrder, itemKey: number): number => {
        const items = this._data.getProperty("Items");
        if (!items || items.length === 0)
            return 0;

        // Whole-pizza (non-halfable) modifiers such as a crust are stored on every half but priced once, so the
        // dedup set is shared across all of the item's halves — each such modifier's upcharge is counted once.
        const countedWholePizzaMods = new Set<string>();

        let upcharge = 0;
        for (const oItem of order.Items.filter((x: IOrderItem) => x.ItemKey === itemKey)) {
            const mItem = this._getItem(oItem.ItemId, items);
            if (!mItem)
                continue;

            upcharge += this._getOrderItemUpcharge(mItem, oItem, countedWholePizzaMods);
        }
        return Util.Float2(upcharge);
    }

    /**
     * Bug 82910: sums the fixed AddsToPrice upcharges on a single order item (one half of a pizza). Whole-pizza
     * (non-halfable) modifiers are deduped via the shared countedWholePizzaMods set so a crust stored on every
     * half is counted once; half-able modifiers (toppings) are counted per half. Extracted from
     * _getItemUpchargeTotal so the outer item loop stays flat.
     */
    private readonly _getOrderItemUpcharge = (mItem: IDataItem, oItem: IOrderItem, countedWholePizzaMods: Set<string>): number => {
        const displayGroups = this._data.getProperty("ModifierDisplayGroups") ?? [];

        // A modifier's display group carries the Back Office "Half & Half" flag (IsHalfable). A non-halfable
        // group applies to the whole pizza (e.g. a crust). A not-found group defaults to non-halfable (count
        // once) — the safe choice that avoids the Bug 82910 double-charge.
        const isDisplayGroupHalfable = (displayGroupId: number): boolean => {
            const group = displayGroups.find(g => g.ModifierDisplayGroupId === displayGroupId);
            return group ? group.IsHalfable : false;
        };

        let upcharge = 0;
        for (const iMod of oItem.Modifiers) {
            const modDispGroup = mItem.ModifierDisplayGroups.find(x => x.ModifierDisplayGroupId == iMod.ModifierDisplayGroupId);
            const mod = modDispGroup?.Modifiers.find(x => x.ModifierId == iMod.ModifierId) ?? null;
            if (!mod?.AddsToPrice)
                continue;

            // Whole-pizza (non-halfable) modifiers are duplicated across the halves — count the upcharge once.
            if (!isDisplayGroupHalfable(iMod.ModifierDisplayGroupId)) {
                const key = `${iMod.ModifierDisplayGroupId}-${iMod.ModifierId}`;
                if (countedWholePizzaMods.has(key))
                    continue;
                countedWholePizzaMods.add(key);
            }

            upcharge += this._getModifierUpchargeAmount(mod, iMod, oItem.SizeId);
        }
        return upcharge;
    }

    /**
     * Bug 82910: the upcharge a single AddsToPrice modifier contributes to its item — the modifier's size
     * AddToPrice scaled by its pre-modifier price-count multiplier. Non-chargeable display groups are pinned
     * to a multiplier of 1, matching the item-price calculator.
     */
    private readonly _getModifierUpchargeAmount = (mod: IItemModifierDisplayGroupModifier, iMod: IOrderItemModifier, sizeId: number): number => {
        const preModifiers = this._data.getProperty("PreModifiers") ?? [];
        const nonChargeableDisplayGroups = this._data.getProperty("Settings")?.NCDG ?? [];
        const preMod = OnlineOrderingUtil.GetPreMod(iMod.PreModifierId, preModifiers);
        let preModPriceMultiplier = preMod ? Number(preMod.PriceCountMultiplier) : 1;
        if (nonChargeableDisplayGroups.includes(`-${iMod.ModifierDisplayGroupId}-`))
            preModPriceMultiplier = 1;

        return preModPriceMultiplier * OnlineOrderingUtil.GetModifierAddToPrice(mod, sizeId);
    }

    private _qualifyCouponItemsGroupQTY = (order: IOrder, coupon: IDataCoupon, group: number, autoApplying: boolean, qualifyAnyQTY: boolean): IvItem[] | null => {
        let vItems: IvItem[] = [];
        const itemsToQualify = coupon.Items.filter(item => item.GroupIndex === group);
        let skipMixMatchCheck = false;
        for (let i = 0; i < itemsToQualify.length; i++) {
            const cItem = itemsToQualify[i];
            const qty = cItem.Quantity;
            for (let j = 0; j < qty; j++) {
                const vItem = this._qualifyCouponItemsGroup(order, coupon, group, cItem.ItemIndex, autoApplying);
                if (vItem) {
                    vItems.push(vItem);
                } else if (!qualifyAnyQTY) {
                    this._releaseMarkedQualifiedItems(order, vItems);
                    vItems = [];
                    break;
                }

                if (!vItem && qualifyAnyQTY) {
                    skipMixMatchCheck = true;
                }
            }

            // Bug 75837 (recurrence of 43598): a fixed-price coupon lists every item it is good for
            // (e.g. "Large BYO or Specialty for $20") and some of those items cost less than the price point.
            // An item the price point can't reach will be released and will continue to keep looking.
            if (vItems.length > 0 && !this._canFixedPriceCouponDiscountItems(order, coupon, vItems)) {
                this._releaseMarkedQualifiedItems(order, vItems);
                vItems = [];
                continue;
            }

            if (vItems.length > 0 && !qualifyAnyQTY)
                return vItems;

            // if item found and coupon is mix and match look for same item again --i
            if (coupon.CouponTypeId == CouponTypeList.MIX_AND_MATCH_PRICE || coupon.CouponTypeId == CouponTypeList.MIX_AND_MATCH_PERCENTAGE) {
                if (vItems.length > 0 && qualifyAnyQTY && !skipMixMatchCheck) {
                    --i;
                } else {
                    skipMixMatchCheck = false;
                }
            }
        }

        return vItems.length > 0 ? vItems : null;
    }

    private _qualifyCouponItemsGroup = (order: IOrder, coupon: IDataCoupon, group: number, itemIndex: number, autoApplying: boolean): IvItem | null => {
        let qItem: IQualifyItemExt = { Index: -1, QTYIndex: -1 };
        let vItem;
        const cItem = coupon.Items.find(x => x.GroupIndex === group && x.ItemIndex === itemIndex);
        if (cItem) {
            let mCount = cItem.ModifierCount;
            let addOnPrice = cItem.AddOnPrice;
            qItem = this._findCouponItem(order, coupon, cItem.ItemId, cItem.SizeId, cItem.ModifierCount);
            if (qItem.Index === -1 && cItem.ModifierCount > 0 && ((!autoApplying && !coupon.ExactMatch) || (autoApplying && !coupon.AutoApplyExactMatch))) {
                while (mCount > 0) {
                    mCount -= 1;
                    qItem = this._findCouponItem(order, coupon, cItem.ItemId, cItem.SizeId, mCount);
                    if (qItem.Index > -1) {
                        const cItemOverride = coupon.Items.find(x => x.GroupIndex === group && x.ItemId == qItem.CouponItemID && x.ModifierCount == mCount);
                        if (cItemOverride) {
                            addOnPrice = cItemOverride.AddOnPrice;
                        }
                        break;
                    }
                }
            }

            if (qItem.Index > -1) {
                vItem = this._getQualifiedCouponItem(order, qItem, cItem.ItemId, cItem.SizeId, mCount, addOnPrice, coupon.ExcludeLoyaltyUsage);
                if (vItem)
                    vItem.CouponItemsGroupID = group;
            }
        }

        return vItem || null;
    }

    private _getTotalCouponItemsPrices = (qualifiedList: IvItem[]): ITotalCouponPrice => {
        let couponItemPrice = 0.0;
        let orderItemPrice = 0.0;
        let couponAddOnPrice = 0.0;

        for (const item of qualifiedList) {
            couponItemPrice += Util.Float2(item.cPrice);
            orderItemPrice += Util.Float2(item.oPrice);
            couponAddOnPrice += Util.Float2(item.AddOn);
        }

        return {
            CouponItemPrice: Util.Float2(couponItemPrice),
            OrderItemPrice: Util.Float2(orderItemPrice),
            CouponAddOnPrice: Util.Float2(couponAddOnPrice),
        };
    }

    private _splitDiscount(order: IOrder, qualifiedList: IvItem[], totalDiscountedItemsPrice: number, discountAmount: number, coupon: IDataCoupon, cDiscount: number): void {
        let tSplit = 0.00;
        if (coupon.IsBeforeTax) {
            const state: BeforeTaxDiscountState = {
                tSplit: 0.00,
                indexToClearRounding: -1,
                discountToRedistribute: 0.00,
                actualDiscountAmount: discountAmount,
                appliedByItemIndex: new Map<number, number>()
            };

            this._splitDiscountBeforeTax(state, order, qualifiedList, totalDiscountedItemsPrice, discountAmount, coupon, cDiscount);
        } else {
            for (const element of qualifiedList) {
                if (element.cPrice > 0) {
                    //Bug 84567: a qualified entry is a single unit (or half), so its share of the discount
                    //can never exceed that unit's own price. Without this a flat-amount coupon worth more
                    //than the unit puts the whole coupon amount on one unit and eats the rest of the line.
                    const proportionalDiscount = Util.Float2((element.oPrice * discountAmount / totalDiscountedItemsPrice) - element.AddOn);
                    element.AfterTaxDiscount = Math.min(proportionalDiscount, Util.Float2(element.oPrice));
                    tSplit += element.AfterTaxDiscount;
                }
            }
            //Bug 84567: only clear the rounding discrepency onto the last item if it still has room,
            //otherwise the amount the cap above just withheld would be handed straight back.
            const lastItem = qualifiedList[qualifiedList.length - 1];
            const remainingRounding = Util.Float2(discountAmount - tSplit);
            if (Util.Float2(lastItem.AfterTaxDiscount + remainingRounding) <= Util.Float2(lastItem.oPrice))
                lastItem.AfterTaxDiscount += remainingRounding; // this is to clear rounding discrepency
        }
    }

    private _splitDiscountBeforeTax(state: BeforeTaxDiscountState, order: IOrder, qualifiedList: IvItem[], totalDiscountedItemsPrice: number, discountAmount: number, coupon: IDataCoupon, cDiscount: number): void {
        const isBOGO = coupon.IsBOGO;
        const couponTypeID = coupon.CouponTypeId;
        const qualifiedListDiscountNotMaxed: IvItem[] = [];
        let orderItemsAmount = 0;

        for (const qItem of qualifiedList) {
            const halfCount = (qItem.HalfCount ?? 1);
            const oItem = order.Items.find(x => x.ItemKey === qItem.ItemKey);
            if (!oItem)
                return;

            orderItemsAmount += qItem.oPrice;
            if (qItem.cPrice > cDiscount && couponTypeID == CouponTypeList.QUANTITY_DISCOUNT) {
                qItem.BeforeTaxDiscount = Util.Float2(cDiscount / halfCount);
                state.tSplit += qItem.BeforeTaxDiscount;
            } else if (qItem.cPrice > cDiscount && (
                couponTypeID == CouponTypeList.QUANTITY_PRICE ||
                couponTypeID == CouponTypeList.MIX_AND_MATCH_PRICE)) {
                state.tSplit = this._calculateQuantityOrMixMatchSplit(qItem, cDiscount, halfCount, state.tSplit);
            } else if (qItem.cPrice > 0 ||
                couponTypeID == CouponTypeList.MANAGER_DISCOUNT ||
                couponTypeID == CouponTypeList.NEXT_ORDER_CREDIT ||
                couponTypeID === CouponTypeList.LOYALTY_DISCOUNT ||
                couponTypeID === CouponTypeList.EMPLOYEE_DISCOUNT ||
                couponTypeID === CouponTypeList.EMPLOYEE_CONSUMPTION ||
                couponTypeID === CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER ||
                couponTypeID === CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY ||
                (isBOGO && couponTypeID == CouponTypeList.PERCENTAGE_OFF_AN_ITEM_IN_CATEGORY_OR_BOGO_IN_CATEGORY)) {
                this._applyBeforeTaxDiscount(qItem, oItem, discountAmount, totalDiscountedItemsPrice, state, qualifiedListDiscountNotMaxed);
            }
        }

        //Don't redistribute any other discount amounts when max discount has been reached for items
        if (state.tSplit === orderItemsAmount)
            return;

        this._handleDiscountRoundingAndRedistribution(
            order,
            qualifiedList,
            qualifiedListDiscountNotMaxed,
            state,
            coupon,
            cDiscount
        );
    }

    private _calculateQuantityOrMixMatchSplit(qItem: IvItem, cDiscount: number, halfCount: number, tSplit: number): number {
        let tmpDiscountAmount = 0;
        if (qItem.cPrice > qItem.oPrice)
            tmpDiscountAmount = qItem.oPrice - (cDiscount / halfCount) - qItem.AddOn;
        else
            tmpDiscountAmount = qItem.cPrice - (cDiscount / halfCount) - qItem.AddOn;
        if (tmpDiscountAmount > 0) {
            qItem.BeforeTaxDiscount = Util.Float2(tmpDiscountAmount);
            tSplit += qItem.BeforeTaxDiscount;
        }
        return tSplit;
    }

    private _applyBeforeTaxDiscount(
        qItem: IvItem,
        oItem: IOrderItem,
        discountAmount: number,
        totalDiscountedItemsPrice: number,
        state: BeforeTaxDiscountState,
        qualifiedListDiscountNotMaxed: IvItem[]
    ): void {
        const beforeTaxDiscount = Util.Float2(qItem.oPrice * discountAmount / totalDiscountedItemsPrice);
        //Bug 84567: a qualified entry is a single unit (or half), so this coupon may never give it more
        //than that unit's own price. The old ceiling was the whole line (qItem.oPrice * oItem.Quantity),
        //which let a flat-amount coupon that qualified 1 of 10 units absorb the value of all 10.
        const unitRoom = Util.Float2(qItem.oPrice - (qItem.BeforeTaxDiscount + qItem.AfterTaxDiscount));
        //the line as a whole still cannot be discounted past its value, counting discounts applied
        //previously by other coupons plus what this coupon already put on other units of the same line
        const appliedToLine = state.appliedByItemIndex.get(qItem.ItemArrayIndex) ?? 0;
        const lineRoom = Util.Float2((qItem.oPrice * oItem.Quantity) - (oItem.AfterTaxDiscount + oItem.BeforeTaxDiscount) - appliedToLine);
        const room = Math.min(unitRoom, lineRoom);
        //apply the discount only while the unit and its line still have room for it
        if (room > 0) {
            const actualDiscount = Math.min(room, beforeTaxDiscount);
            const overDiscountedAmount = Util.Float2(beforeTaxDiscount - actualDiscount);
            qItem.BeforeTaxDiscount = Util.Float2(qItem.BeforeTaxDiscount + actualDiscount);
            state.tSplit = Util.Float2(state.tSplit + actualDiscount);
            state.appliedByItemIndex.set(qItem.ItemArrayIndex, Util.Float2(appliedToLine + actualDiscount));

            //update the state to determine how much to redistribute to items that didn't reach the max discount possible
            if (overDiscountedAmount > 0) {
                state.discountToRedistribute = Util.Float2(state.discountToRedistribute + overDiscountedAmount);
            } else {
                qualifiedListDiscountNotMaxed.push(qItem);
            }

            //check if the discounts still don't match the order's item price, if so use this item for performing rounding 
            if (state.indexToClearRounding === -1 && oItem.BeforeTaxDiscount + qItem.BeforeTaxDiscount < qItem.oPrice) {
                state.indexToClearRounding = qItem.ItemArrayIndex;
            }
        } else {
            state.discountToRedistribute = Util.Float2(state.discountToRedistribute + beforeTaxDiscount);
        }
    }

    private _handleDiscountRoundingAndRedistribution(
        order: IOrder,
        qualifiedList: IvItem[],
        qualifiedListDiscountNotMaxed: IvItem[],
        state: BeforeTaxDiscountState,
        coupon: IDataCoupon,
        cDiscount: number
    ): void {
        if (state.discountToRedistribute > 0 && qualifiedListDiscountNotMaxed.length > 0) {

            //calculate new totalDiscountedItemsPrice
            let totalDiscountedItemsPrice = 0;
            for (const qItem of qualifiedListDiscountNotMaxed) {
                totalDiscountedItemsPrice = Util.Float2(totalDiscountedItemsPrice + qItem.oPrice);
            }
            const discountToRedistribute = Util.Float2(state.discountToRedistribute);

            //reset state
            state.discountToRedistribute = 0;   
            state.indexToClearRounding = -1;
            this._splitDiscountBeforeTax(
                state,
                order,
                qualifiedListDiscountNotMaxed,
                totalDiscountedItemsPrice,
                discountToRedistribute,
                coupon,
                cDiscount
            );
        } else {
            const remainingRounding = Util.Float2(Util.Float2(state.actualDiscountAmount) - Util.Float2(state.tSplit));
            if (remainingRounding != 0) {
                const qItem = qualifiedList.find(x => x.ItemArrayIndex === state.indexToClearRounding);
                if (!qItem)
                    return;

                qItem.BeforeTaxDiscount += remainingRounding;
            }
        }
    }

    private _getTotalQualifiedItemsDiscountAmount(qualifiedList: IvItem[], discountAmount: number): number {
        const actualBeforeTaxDiscountAmount = qualifiedList.reduce((accumulator, currentItem) => {
            const beforeTax = currentItem["BeforeTaxDiscount"];
            if (typeof beforeTax === 'number') {
                return Util.Float2(accumulator + beforeTax);
            }
            return accumulator; 
        }, 0);

        const actualAfterTaxDiscountAmount = qualifiedList.reduce((accumulator, currentItem) => {
            const beforeTax = currentItem["AfterTaxDiscount"];
            if (typeof beforeTax === 'number') {
                return Util.Float2(accumulator + beforeTax);
            }
            return accumulator; 
        }, 0);

        const actualDiscountAmount = Util.Float2(actualBeforeTaxDiscountAmount + actualAfterTaxDiscountAmount);
        if (actualDiscountAmount < discountAmount)
            return actualDiscountAmount;

        return discountAmount;
    }

    private _addDiscountsToItems(order: IOrder, qualifiedList: IvItem[], isPriceReducer: boolean, isReward: boolean): void {
        for (const item of qualifiedList) {
            const oItem = order.Items[item.ItemArrayIndex];
            if (!oItem)
                continue;

            if (isPriceReducer) {
                oItem.Price -= item.BeforeTaxDiscount;
                oItem.TaxAmount -= item.BeforeTaxDiscount;
                oItem.MenuPrice -= item.BeforeTaxDiscount;

                oItem.Price -= item.AfterTaxDiscount;
                oItem.MenuPrice -= item.AfterTaxDiscount;
            } else {
                oItem.BeforeTaxDiscount += item.BeforeTaxDiscount;
                oItem.AfterTaxDiscount += item.AfterTaxDiscount;
            }

            if (isReward) {
                oItem.LoyaltyDiscountAmount = Util.Float2(oItem.LoyaltyDiscountAmount + Util.Float2(oItem.BeforeTaxDiscount + oItem.AfterTaxDiscount));
            }
        }
    }

    private _addCouponToOrder(order: IOrder, addCouponToOrderParams: IAddCouponToOrderParams): number {
        const oCoupon: IOrderCoupon = {
            CouponKey: (order.Coupons.length === 0) ? 1 : order.Coupons[order.Coupons.length - 1].CouponKey + 1,
            CouponId: addCouponToOrderParams.couponId,
            CouponCode: addCouponToOrderParams.couponCode,
            Discount: addCouponToOrderParams.discount,
            Amount: addCouponToOrderParams.discountAmount,
            IsBeforeTax: addCouponToOrderParams.isBeforeTax,
            IsActive: addCouponToOrderParams.isActive,
            AllowOtherCoupons: addCouponToOrderParams.allowOtherCoupons,
            ExcludeLoyaltyUsage: addCouponToOrderParams.excludeLoyaltyUsage,
            IsAdded: false,
            IsRemoved: false,
            AutoApplied: addCouponToOrderParams.isAutoApplying || false,
            ChannelID: addCouponToOrderParams.channelId || null,
            IsPromotionCode: addCouponToOrderParams.isPromotionCode || false,
            IsCampaignCode: addCouponToOrderParams.isCampaignCode || false,
            RewardId: addCouponToOrderParams.rewardId || null,
            IsBankedCurrency: addCouponToOrderParams.isBankedCurrency || false,
            RedemptionReference: null,
            BasketItemId: null,
            SourceId: addCouponToOrderParams.sourceId ?? null,
            IsSpecialCode: addCouponToOrderParams.isSpecialCode || false,
            IsSingleUseCoupon: addCouponToOrderParams.isSingleUseCoupon || false,
        };
        order.Coupons.push(oCoupon);
        return oCoupon.CouponKey;
    }

    private _addCouponToItemsCoupon = (order: IOrder, qualifiedList: IvItem[], couponKey: number, priority: number, isActive: boolean, allowOtherCoupons: number): void => {
        for (const item of qualifiedList) {
            const itemCoupon = {
                ItemKey: item.ItemKey,
                HalfIndex: item.HalfIndex,
                ItemRecId: item.ItemRecID,
                CouponKey: couponKey,
                QTYIndex: item.QTYIndex,
                BeforeTaxDiscount: item.BeforeTaxDiscount,
                AfterTaxDiscount: item.AfterTaxDiscount,
                DiscountedQuantity: allowOtherCoupons,
                IsActive: isActive,
                Priority: priority,
            };
            order.ItemsCoupons.push(itemCoupon);
        }
    }

    private _markAllHalves = (order: IOrder, itemKey: number): void => {
        for (const oItem of order.Items) {
            if (oItem.ItemKey === itemKey)
                oItem.DiscountedMarkedQuantity += 1;
        }
    }

    /**
     * Releases the marked quantities held by qualified items decided not to be used, so the
     * items look untouched to the coupon items (and coupons) tried after them.
     */
    private readonly _releaseMarkedQualifiedItems = (order: IOrder, vItems: IvItem[]): void => {
        for (const vItem of vItems) {
            const oItem = order.Items[vItem.ItemArrayIndex];
            if (!oItem)
                continue;

            if (oItem.HalfCount > 1)
                this._unMarkAllHalves(order, oItem.ItemKey);
            else
                oItem.DiscountedMarkedQuantity -= 1;
        }
    }

    /**
     * Whether a fixed-price coupon can take anything off the items just qualified for one of its coupon
     * items (Bug 75837). Non-fixed-price coupons always can: every other type derives its discount from
     * the item's own price rather than measuring the item against a price point.
     *
     * Deliberately computed the same way the discount itself is computed: the covered price, capped at
     * the order price for ExactMatch, less the price point and the add-on. A coupon item is only
     * passed over when applying it really would have produced nothing.
     *
     * Only a single-group coupon can be judged this way. The price point is one whole-coupon figure, so
     * it is only meaningful against the coupon's COMPLETE qualified set. With one group these items are
     * that set. Across several groups each group holds just a part of it. A "Large Pizza + 2-Liter for
     * $20" would weigh the 2-liter alone against $20 and hand back a group that qualified perfectly
     * well, failing the coupon. Multi-group coupons keep the plain first-match behavior.
     */
    private readonly _canFixedPriceCouponDiscountItems = (order: IOrder, coupon: IDataCoupon, vItems: IvItem[]): boolean => {
        if (coupon.CouponTypeId != CouponTypeList.FIXED_PRICE || vItems.length == 0)
            return true;

        if (new Set(coupon.Items.map(x => x.GroupIndex)).size > 1)
            return true;

        let couponItemPrice = 0.0;
        let orderItemPrice = 0.0;
        let addOnPrice = 0.0;

        for (const vItem of vItems) {
            const prices = this._getFixedPriceItemPrices(order, coupon, vItem);
            couponItemPrice = Util.Float2(couponItemPrice + prices.CouponItemPrice);
            orderItemPrice = Util.Float2(orderItemPrice + prices.OrderItemPrice);
            addOnPrice = Util.Float2(addOnPrice + vItem.AddOn);
        }

        let cItemPrice = couponItemPrice;
        if (coupon.ExactMatch && cItemPrice > orderItemPrice)
            cItemPrice = orderItemPrice;

        return Util.Float2(cItemPrice - Util.Float2(coupon.Discount) - addOnPrice) > 0;
    }

    /**
     * The covered and order prices a single qualified item will contribute to the fixed-price
     * calculation, as they will stand by the time the discount is computed.
     *
     * A whole item contributes its own coupon and order price. A half & half item is still a single
     * qualified item here (_qualifyAllHalfs hasn't split it yet) and comes out of that split priced
     * at what the customer is charged for the whole item, less the priced modifiers and upcharges the
     * coupon doesn't cover.
     */
    private readonly _getFixedPriceItemPrices = (order: IOrder, coupon: IDataCoupon, vItem: IvItem): { CouponItemPrice: number, OrderItemPrice: number } => {
        const halfCount = vItem.HalfCount || 1;
        if (halfCount <= 1)
            return { CouponItemPrice: Util.Float2(vItem.cPrice), OrderItemPrice: Util.Float2(vItem.oPrice) };

        const itemTotal = order.Items
            .filter(oItem => oItem.ItemKey === vItem.ItemKey)
            .reduce((total, oItem) => Util.Float2(total + oItem.Price), 0.0);

        return {
            CouponItemPrice: Util.Float2(itemTotal - this._getPricedModifierOverageTotal(order, coupon, vItem)),
            OrderItemPrice: itemTotal
        };
    }

    private _unMarkAllHalves = (order: IOrder, itemKey: number): void => {
        for (const oItem of order.Items) {
            if (oItem.ItemKey == itemKey)
                oItem.DiscountedMarkedQuantity -= 1;
        }
    }

    private _clearMarkedItems = (order: IOrder): void => {
        for (const oItem of order.Items) {
            oItem.DiscountedMarkedQuantity = 0;
        }
    }

    private _useMarkedItems = (order: IOrder): void => {
        for (const oItem of order.Items) {
            oItem.DiscountedQuantity += oItem.DiscountedMarkedQuantity;
            oItem.DiscountedMarkedQuantity = 0;
        }
    }

    private async _autoApplyOrderCoupons(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponIdsToSkip: number[] = []): Promise<void> {
        for (const coupon of order.Coupons) {
            if (couponIdsToSkip.includes(coupon.CouponId))
                continue;

            if (!coupon.RewardId) {
                // BUG: 68816 - Note: If a Mix & Match coupon was applied to the order first and then a Percentage Off Entire Order coupon, then an item that is applicable for the Percentage Off Entire Item is 
                // added to the cart, the Mix & Match coupon will successfully still re-apply to the order. This is because the Percentage Off Entire Order coupon has not been
                // attempted to reapply to all items at this point. Visually the coupons will look like they are changing order, but discounts won't be affected.
                const mCoupon = this.GetCoupon(coupon.CouponId, true, order.OrderTypeID, coupon.ChannelID);
                const couponTypesList = [CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER, CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY, CouponTypeList.MIX_AND_MATCH_PRICE, CouponTypeList.MIX_AND_MATCH_PERCENTAGE];
                if (mCoupon && couponTypesList.includes(mCoupon.CouponTypeId)) {
                    await this.RemoveCouponByKey(order, incompleteDiscounts, loyaltyProvider, coupon.CouponKey, false, false);
                    const params: IApplyCouponByIdParams = {
                        order: order,
                        incompleteDiscounts: incompleteDiscounts,
                        loyaltyProvider: loyaltyProvider,
                        couponId: coupon.CouponId,
                        couponCode: coupon.CouponCode,
                        channelId: coupon.ChannelID,
                        isCampaignCode: coupon.IsCampaignCode,
                        isPromotionCode: coupon.IsPromotionCode,
                        rewardId: coupon.RewardId,
                        isBankedCurrency: coupon.IsBankedCurrency,
                        sourceId: coupon.SourceId,
                        isSUCValidationReq: Coupon.SucValidationNeeded(coupon.IsPromotionCode, coupon.CouponCode),
                        isSpecialCode: coupon.IsSpecialCode ?? false,
                    };
                    await this.ApplyCouponByID(params);
                }
            }

            if (coupon.RewardId) {
                const reward = this._user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId == coupon.RewardId);
                if (!reward)
                    continue;

                await loyaltyProvider.removeReward(reward);
                await loyaltyProvider.addReward(reward);
            } else if (coupon.IsBankedCurrency) {
                const amount = coupon.Amount;
                await loyaltyProvider.removeBankedCurrency();
                await loyaltyProvider.applyBankedCurrency(amount);
            }
        }
    }

    public AutoApplyCoupons = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponIdsToSkip: number[] = []): Promise<void> => {
        await this._autoApplyOrderCoupons(order, incompleteDiscounts, loyaltyProvider, couponIdsToSkip);

        // Try to Apply Incomplete Coupons
        const tempDiscounts = Util.deepClone(incompleteDiscounts) as IIncompleteDiscount[];
        for (const coupon of tempDiscounts) {
            if (coupon.rewardId) {
                const reward = this._user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId == coupon.rewardId);
                if (reward) {
                    await loyaltyProvider.addReward(reward);
                    //Refactor: OnlineOrderingUtil.GUI_SetOrder_Total(true, aOLO, aOLOModules);
                    break;
                }
            }

            const params: IApplyCouponByIdParams = {
                order: order,
                incompleteDiscounts: incompleteDiscounts,
                loyaltyProvider: loyaltyProvider,
                couponId: coupon.couponId,
                couponCode: coupon.couponCode ?? null,
                channelId: coupon.channelId ?? null,
                autoApplying: true,
                isCampaignCode: coupon.isCampaignCode ?? false,
                isPromotionCode: coupon.isPromotionCode ?? false,
                sourceId: coupon.sourceId ?? null,
                rewardId: coupon.rewardId ?? null,
                isBankedCurrency: coupon.isBankedCurrency ?? false,
                isSUCValidationReq: false,
                isSpecialCode: coupon.isSpecialCode ?? false
            };
            const result = await this.ApplyCouponByID(params);
            if (result)
                await this.RemoveIncompleteCouponById(order, incompleteDiscounts, loyaltyProvider, coupon.couponId, true);
        }

        const autoApplyCoupons: IDataCoupon[] = this._data.getProperty("Coupons").filter(cpn => cpn.IsAutoApply);
        for (const coupon of autoApplyCoupons) {
            const params: IApplyCouponByIdParams = {
                order: order,
                incompleteDiscounts: incompleteDiscounts,
                loyaltyProvider: loyaltyProvider,
                couponId: coupon.CouponId,
                autoApplying: true,
                channelId: coupon.ChannelID ?? null,
                isCampaignCode: false,
                isPromotionCode: false,
                rewardId: null,
                isBankedCurrency: false,
                sourceId: null,
                isSUCValidationReq: false,
                couponCode: null,
                isSpecialCode: false
            };
            await this.ApplyCouponByID(params);
        }
    }

    public RemoveIncompleteCouponById = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponId: number, appliedToOrder: boolean = false): Promise<void> => {
        const item = incompleteDiscounts.find(x => x.couponId === couponId);
        if (!item)
            return;

        if (item.isBankedCurrency) {
            await loyaltyProvider.removeBankedCurrency();
        }

        if (item.rewardId) {
            const reward = this._user.getProperty("LoyaltyData")?.Rewards.find(x => x.RewardId === item.rewardId);
            if (reward) {
                reward.Used = false;
                this._eventBus.emit(EventTypes.REWARD_REMOVED, reward.RewardId);
            }
        }

        if (!appliedToOrder) {
            const offers = this._user.getProperty("Offers");
            if ((item.isCampaignCode || item.isPromotionCode) && offers?.Codes) {
                const offer = offers.Codes.find(x => x.CouponCode.toLowerCase() == Coupon.SanitizeSucCouponCode(item.couponCode));
                if (offer)
                    offer.Used = false;
            }

            if (item.sourceId === 1) {
                const nextOrderCredit = this._user.getProperty("NextOrderCredits")?.find(x => x.CouponID == item.couponId && x.CustomerCreditID.toString() === item.couponCode);
                if (nextOrderCredit)
                    nextOrderCredit.Used = false;
            }
        }

        const index = incompleteDiscounts.findIndex(x => x.couponId === couponId);
        if (index > -1) {
            // invalidate if single use coupon
            const couponCode = incompleteDiscounts[index].couponCode || "";
            if (couponCode.toLowerCase().includes("suc_"))
                await this._invalidateSingleUseCoupon(order, couponCode);

            incompleteDiscounts.splice(index, 1);
        }
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public GetCoupon = (couponId: number, checkOTST: boolean, orderTypeID: number | null, channelID: number | null): IDataCoupon | null => {
        const coupon = this._data.getProperty("Coupons").find(cpn => cpn.CouponId === couponId);
        if (coupon) {
            coupon.ChannelID = channelID || null;
        }
        if (checkOTST && coupon?.OrderTypeSubTypes && coupon?.OrderTypeSubTypes.length > 0)
            return (coupon.OrderTypeSubTypes.find(x => x.OrderTypeId === orderTypeID) ? coupon : null);

        return coupon || null;
    }

    public CheckCouponOTST = (coupon: IDataCoupon, orderTypeID: number): boolean => {
        return !!coupon.OrderTypeSubTypes?.find(x => x.OrderTypeId === orderTypeID);
    }

    public VerifyCouponDate = (order: IOrder, coupon: IDataCoupon): boolean => {
        if (coupon.StartDate == null && coupon.EndDate == null)
            return true;

        const tempOrderDate = order.FutureDate ? order.FutureDate : order.OrderStartTime;
        const orderDate = new Date(new Date(Util.deepClone(tempOrderDate)).setHours(0, 0, 0, 0));
        if (orderDate.getTime() >= new Date(coupon.StartDate?.replaceAll("-", "/") || "").getTime() && orderDate.getTime() <= new Date(coupon.EndDate?.replaceAll("-", "/") || "").getTime())
            return true;

        return false;
    }

    public CheckCouponSCHS = (order: IOrder, coupon: IDataCoupon): boolean => {
        if (!coupon.IsScheduled)
            return true;

        let promiseDate;
        let dayOfWeek = 0;
        let promiseMinutes = 0;
        if (order.FutureDate === null) {
            const timeOffset = this._data.getProperty("TimeOffset");
            promiseDate = Util.NowStore(timeOffset);
            const promiseBDate = new Date(Util.GetBUSDate(promiseDate, timeOffset, this._data.getProperty("Settings").BSM));
            dayOfWeek = promiseBDate.getDay();
            promiseMinutes = promiseDate.getMinutes() + (60 * promiseDate.getHours());
        } else {
            promiseDate = new Date(order.FutureDate);
            dayOfWeek = promiseDate.getDay();
            promiseMinutes = order.FutureMinute;
        }

        return coupon.Schedules ? coupon.Schedules.some(schedule => {
            return schedule.WeekDayNumber === dayOfWeek && schedule.IsActive && schedule.StartDayMinute <= promiseMinutes && schedule.EndDayMinute >= promiseMinutes;
        }) : true;
    }

    public CheckCouponProfileRequired(coupon: IDataCoupon): boolean {
        return !coupon.IsProfileRequired || (coupon.IsProfileRequired && this._user.isLoggedIn() && this._user.getProperty("ProfileId") != 0);
    }

    public CheckCouponHasQualifyingItems = async (eventBus: EventEmitter, order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, params: ICheckCouponHasQualifyingItemsParams): Promise<IIncompleteDiscount | null> => {
        let incompleteResult = false;
        let coupon = this.GetCoupon(params.couponId, true, order.OrderTypeID, params.channelId ?? null);

        if (!coupon && params.reward) {
            await this.loadCouponByIdAsync(params.reward.CouponId);
            coupon = this.GetCoupon(params.couponId, true, order.OrderTypeID, params.channelId ?? null);
        }

        //If box topper, force change the order type to a type supported by the coupon to allow applying
        if (!coupon && params.orderId){
            coupon = this.GetCoupon(params.couponId, false, null, params.channelId ?? null);
            if (coupon)
                await this._changeOrderTypeForCoupon(order, coupon, loyaltyProvider, null);
            return null;
        }

        if (!coupon)
            return null;

        if (loyaltyProvider.onlyOneDiscountAllowed(params.reward?.RewardId ?? null, params.couponId))
            return null;

        if (coupon.Items.length > 0) {
            const tempParams: IApplyCouponByIdParams = {
                order: order,
                incompleteDiscounts: incompleteDiscounts,
                loyaltyProvider: loyaltyProvider,
                couponId: params.couponId,
                showApplyMsg: params.showApplyMsg ?? true,
                // Forced false: a failed attempt here just opens the meal deal dialog, not a real error.
                showErrorMsg: false,
                couponCode: params.couponCode ?? null,
                isCampaignCode: params.isCampaignCode ?? false,
                isPromotionCode: params.isPromotionCode ?? false,
                rewardId: params.reward ? params.reward.RewardId : null,
                sourceId: params.sourceId ?? null,
                channelId: params.channelId ?? null,
                isBankedCurrency: false,
                isSpecialCode: params.isSpecialCode ?? false,
                isSUCValidationReq: params.sucValidationReq ?? Coupon.SucValidationNeeded((params.isPromotionCode ?? false), (params.couponCode ?? null)),
                isSingleUseCoupon: params.isSingleUseCoupon ?? false,
            };
            const applied = await this.ApplyCouponByID(tempParams, eventBus);

            if (applied) {
                await this.RemoveIncompleteCouponById(order, incompleteDiscounts, loyaltyProvider, params.couponId, true);
            } else {
                const dto: IDealBuilderCouponDTO = {
                    coupon: coupon,
                    isCampaignCode: params.isCampaignCode ?? false,
                    isPromotionCode: params.isPromotionCode ?? false,
                    offerCode: params.couponCode ?? "",
                    rewardId: params.reward?.RewardId ?? null,
                    sourceId: params.sourceId ?? null,
                    channelId: params.channelId ?? null,
                    isSpecialCode: params.isSpecialCode ?? false,
                    isSingleUseCoupon: params.isSingleUseCoupon ?? false
                };

                //emit event for opening meal deal dialog
                eventBus.emit(EventTypes.MEAL_DEAL_OPEN, dto);
            }
        } else {
            if (params.couponCode) {
                const result = await this.ApplyCoupon(order, incompleteDiscounts, loyaltyProvider, coupon, params.couponCode, false, true, undefined, undefined, params.isCampaignCode, params.isPromotionCode, null, null, params.sourceId, true, false, false, params.isSpecialCode, params.isSingleUseCoupon);
                if (!result) {
                    incompleteResult = true;
                    //Refactor
                    //    this._functions.addIncompleteCouponToCart(coupon.CouponId, couponCode, aOLO.Temp.IncompleteDiscounts, aOLO, aOLOModules, channelID, isCampaignCode, isPromotionCode);
                } else if (params.offer) {
                    params.offer.Used = true;
                }
            } else if (params.orderId) {
                const result = await this.ApplyCoupon(order, incompleteDiscounts, loyaltyProvider, coupon, `bx${params.orderId}`, false, true, undefined, undefined, params.isCampaignCode, params.isPromotionCode);
                //Refactor
                if (!result)
                    incompleteResult = false;
                //    this._functions.addIncompleteCouponToCart(coupon.CouponId, '', aOLO.Temp.IncompleteDiscounts, aOLO, aOLOModules, channelID, isCampaignCode, isPromotionCode);
            } else if (params.reward) {
                const tempParams: IApplyCouponByIdParams = {
                    order: order,
                    incompleteDiscounts: incompleteDiscounts,
                    loyaltyProvider: loyaltyProvider,
                    couponId: params.couponId,
                    showApplyMsg: params.showApplyMsg ?? true,
                    showErrorMsg: params.showErrorMsg ?? false,
                    isCampaignCode: params.isCampaignCode ?? false,
                    isPromotionCode: params.isPromotionCode ?? false,
                    rewardId: params.reward.RewardId,
                    channelId: params.channelId ?? null,
                    sourceId: params.sourceId ?? null,
                    isBankedCurrency: false,
                    couponCode: params.couponCode ?? null,
                    isSpecialCode: params.isSpecialCode ?? false,
                    isSUCValidationReq: Coupon.SucValidationNeeded((params.isPromotionCode ?? false), (params.couponCode ?? null)),
                };
                const result = await this.ApplyCouponByID(tempParams);
                if (result) {
                    params.reward.Used = true;
                    await this.RemoveIncompleteCouponById(order, incompleteDiscounts, loyaltyProvider, params.couponId);
                }
                // Refactor
                else
                    incompleteResult = true;
                //    this._functions.addIncompleteCouponToCart(coupon.CouponId, "", aOLO.Temp.IncompleteDiscounts, aOLO, aOLOModules, channelID, isCampaignCode, isPromotionCode, reward.RewardId);
            } else {
                const tempParams: IApplyCouponByIdParams = {
                    order: order,
                    incompleteDiscounts: incompleteDiscounts,
                    loyaltyProvider: loyaltyProvider,
                    couponId: params.couponId,
                    showApplyMsg: params.showApplyMsg ?? true,
                    showErrorMsg: params.showErrorMsg ?? false,
                    couponCode: params.couponCode ?? null,
                    isCampaignCode: params.isCampaignCode ?? false,
                    isPromotionCode: params.isPromotionCode ?? false,
                    sourceId: params.sourceId ?? null,
                    channelId: params.channelId ?? null,
                    rewardId: (params.reward as IUserLoyaltyReward | null | undefined)?.RewardId ?? null,
                    isBankedCurrency: false,
                    isSpecialCode: params.isSpecialCode ?? false,
                    isSUCValidationReq: Coupon.SucValidationNeeded((params.isPromotionCode ?? false), (params.couponCode ?? null)),
                };
                const result = await this.ApplyCouponByID(tempParams);
                //Refactor
                if (!result)
                    incompleteResult = true;
                //    this._functions.addIncompleteCouponToCart(coupon.CouponId, "", aOLO.Temp.IncompleteDiscounts, aOLO, aOLOModules, channelID, isCampaignCode, isPromotionCode);
            }
        }

        if (this._dataLayer && !params.reward && !params.offer && !params.sourceId && params.couponCode) {
            this._dataLayer.apply_coupon_code(params.couponCode, params.isPromotionCode ?? false);
        }

        if (incompleteResult) {
            const incompleteDiscount: IIncompleteDiscount = {
                couponId: coupon.CouponId,
                couponCode: params.couponCode ?? '',
                channelId: params.channelId,
                isCampaignCode: params.isCampaignCode,
                isPromotionCode: params.isPromotionCode,
                rewardId: params.reward?.RewardId ?? null,
                sourceId: params.sourceId
            };

            return incompleteDiscount;
        }

        return null;
    }

    public loadCouponByIdAsync = async (couponId: number): Promise<void> => {
        if (!this._data.getProperty("StoreInfo").storeKey)
            return;

        const data = {
            CID: couponId,
            SKY: this._data.getProperty("StoreInfo").storeKey
        };
        const response = await this._orderingService.getCouponById(data);

        if (response.success) {
            const coupons = this._data.getProperty("Coupons");
            coupons.push(this._mapCoupon(response.coupon));
            this._data.setProperty("Coupons", coupons);
        } else if (!response.serverError)
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("UnableToRetrieveReward"),
                true
            );
    }

    private _mapCoupon = (couponJSON: string): IDataCoupon => {
        const cpn = JSON.parse(couponJSON);
        const coupon = {
            CouponId: cpn.CPID,
            CouponTypeId: cpn.CPTID,
            AllowOtherCoupons: cpn.AOC,
            ExcludeLoyaltyUsage: cpn.ELU,
            AutoApplyExactMatch: cpn.AAEXCMCH,
            BackgroundColor: cpn.TBGC,
            BOGOSelectionTypeID: cpn.BGSTID,
            Descriptions: cpn.DSCRs,
            Discount: cpn.DIC,
            ExactMatch: cpn.EXCMCH,
            ImageURL: cpn.IURL,
            IsBeforeTax: cpn.IBTX,
            IsBOGO: cpn.BOGO,
            IsHiddenOnline: cpn.IHDN,
            IsPriceReducer: cpn.IPR,
            IsProfileRequired: cpn.IOPR,
            IsScheduled: cpn.SCH,
            IsVerificationNeeded: cpn.IVN,
            IsAutoApply: cpn.IAA,
            IsAppOnly: false,
            LimitsPerOrder: cpn.LPO,
            MaximumDiscountAmount: cpn.MXAMT,
            MinimumItemQuantity: cpn.MINITMQTY,
            MinimumOrderAmount: cpn.MIAMT,
            CalculationMethodID: cpn.CALCID,
            Names: cpn.NAMs,
            Priority: cpn.PRITY,
            WebCategoryId: cpn.WCID,
            StartDate: cpn.SDATE,
            EndDate: cpn.EDATE,
            Codes: cpn.CCODS?.map((cd: any) => ({
                CouponCode: cd.CCOD
            })) || [],
            Items: cpn.CITMS?.map((itm: any) => ({
                AddOnPrice: itm.AOPRC,
                GroupIndex: itm.GRP,
                ItemId: itm.IID,
                ItemIndex: itm.IIDX,
                ModifierCount: itm.MCNT,
                Quantity: itm.QTY,
                SizeId: itm.SZID
            })) || [],
            ItemCategories: cpn.CITMCATS?.map((cat: any) => ({
                CategoryId: cat.CATID
            })) || [],
            OrderTypeSubTypes: cpn.OTSTS?.map((ot: any) => ({
                OrderTypeId: ot.OT,
                OrderTypeSubId: ot.OST
            })) || [],
            Schedules: cpn.SCHS?.map((sch: any) => ({
                WeekDayNumber: sch.WKDNO,
                IsActive: sch.ISACT,
                StartDayMinute: sch.STRTMIN,
                EndDayMinute: sch.ENDMIN
            })) || [],
            Upgrades: cpn.UGRDS?.map((upg: any) => ({
                BuySizeId: upg.BSZID,
                PriceSizeId: upg.PSZID,
                UpgradeIndex: upg.UGRDIDX
            })) || []
        };

        return coupon;
    }

    public ApplyCouponByCode = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string, isOffer: boolean = false, isPromotion: boolean = false, isCampaign: boolean = false, isSpecialCode: boolean = false):
        Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> => {
        if (couponCode === "") {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("InvalidCouponCode"),
                true
            );
            return { success: false, incompleteDiscount: null };
        }

        couponCode = couponCode.toLowerCase();

        const tempOrderDate = order.FutureDate ?? order.OrderStartTime;
        const orderDate = new Date(new Date(Util.deepClone(tempOrderDate)).setHours(0, 0, 0, 0));

        if (loyaltyProvider.onlyOneDiscountAllowed(null, await this._resolveCouponIdForLimitCheckAsync(order, couponCode, orderDate)))
            return { success: false, incompleteDiscount: null };

        if (!this.isCouponAllowedInFundraiser(order))
            return { success: false, incompleteDiscount: null };

        // If coupon code from User Offers
        if (isPromotion) {
            const offer = this._user.getProperty("Offers")?.Codes?.find(x => x.CouponCode.toLowerCase() === Coupon.SanitizeSucCouponCode(couponCode)) ?? null;
            const singleUseOffer = offer?.IsSingleUseCoupon ?? false;

            const result = await this._applyCouponByPromotionCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, true, orderDate, offer, singleUseOffer);
            return result;
        }

        // If coupon code from User Offers
        if (isCampaign) {
            const result = await this._applyCouponByCampaignCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, isOffer, true);
            return result;
        }

        // Check if normal coupon code, advertisement code, or channel code in that order
        const storeId = !this._data.getProperty("StoreInfo").storeId ? this._data.getBrandProperty("StoreInfo").storeId : this._data.getProperty("StoreInfo").storeId;
        const coupon = await this._getCouponByCode(couponCode, orderDate, storeId, order.GUID);
        if (coupon) {
            const result = await this._applyCouponByCode_ContinueAsync(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, false, false, null, isSpecialCode);
            return result;
        }

        let found = false;
        let incompleteDiscount: IIncompleteDiscount | null = null;

        // If deep link campaign or promotion coupon code
        if (!found) {
            const result = await this._getCouponByCloudDeepLinkAsync(order, incompleteDiscounts, loyaltyProvider, couponCode);
            found = result.success;
            incompleteDiscount = result.incompleteDiscount;
        }

        // Check if coupon is a single use coupon or promotion
        if (!found) {
            const result = await this._applySingleUseOrPromotionCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, orderDate);
            found = result.success;
            incompleteDiscount = result.incompleteDiscount;
        }

        // Check if coupon code is a campaign
        if (!found) {
            const result = await this._applyCouponByCampaignCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, isOffer, false);
            found = result.success;
            incompleteDiscount = result.incompleteDiscount;
        }

        if (!found && this._context.getProperty("cloudCoupon") === undefined) { //skip showing an error if a cloud coupon is still in progress to being applied
            this._eventBus.emit(EventTypes.COUPON_INVALID, "InvalidCoupon"); //message gets localized in cart.ts
            return { success: false, incompleteDiscount: incompleteDiscount };
        }

        return { success: true, incompleteDiscount: incompleteDiscount };
    }

    public isCouponAllowedInFundraiser(order: IOrder, showError: boolean = true): boolean {
        const fundraiser = this._data.getProperty("Fundraisers").find(x => x.FundraiserId == order.Fundraiser?.ID);
        if (fundraiser && !fundraiser.IsCouponAllowed) {
            if (showError)
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("REMOVE_FUNDRAISER_FOR_PROMO_CODE"),
                    true
                );
            return false;
        }

        return true;
    }

    public isCouponAllowedInChallengeEvent(order: IOrder, showError: boolean = true): boolean {
        const event = this._hasEventBlockingCoupons(order);
        if (event.block) {
            if (showError)
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("ERROR_COUPON_INELIGIBLE_FOR_USE_WITH_EVENT").replaceAll("{{event_name}}", event.eventName ?? ""),
                    true
                );
            return false;
        }

        return true;
    }

    private async _getCouponByCloudDeepLinkAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string): Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> {
        const cloudCoupon = this._context.getProperty("cloudCoupon");
        this._context.setProperty("cloudCoupon", undefined);

        if (cloudCoupon?.CouponCode.toLowerCase() !== couponCode)
            return { success: false, incompleteDiscount: null };

        if (cloudCoupon.IsCampaignCode) {
            const coupon = await this._getCouponByCampaignCode(cloudCoupon.CouponCode, cloudCoupon.StoreId, true, order.GUID);
            if (coupon) {
                //check if order type needs to be changed
                const changed = await this._changeOrderTypeForCoupon(order, coupon, loyaltyProvider, cloudCoupon);
                if (changed)
                    return { success: false, incompleteDiscount: null };

                this._context.setProperty("cloudCouponInProgress", false);
                const result = await this._applyCouponByCode_ContinueAsync(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, true, false, null);
                return { success: true, incompleteDiscount: result.incompleteDiscount };
            }
        } else { //Survey coupons path
            const coupon = await this._getCouponByPromotionCode(cloudCoupon.CouponCode, cloudCoupon.StoreId, true, order.GUID);
            if (coupon) {
                //check if order type needs to be changed
                const changed = await this._changeOrderTypeForCoupon(order, coupon, loyaltyProvider, cloudCoupon)
                if (changed)
                    return { success: false, incompleteDiscount: null };

                this._context.setProperty("cloudCouponInProgress", false);

                const result = await this._applyCouponByCode_ContinueAsync(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, false, true, null);
                return { success: true, incompleteDiscount: result.incompleteDiscount };
            }
        }

        this._context.setProperty("cloudCoupon", cloudCoupon);
        return { success: false, incompleteDiscount: null };
    }

    private async _redeemSingleUseCouponAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string, orderDate: Date, showErrorMsg: boolean): Promise<string | null> {
        const response = await this._validateSingleUseCouponAsync(order, couponCode, orderDate, showErrorMsg);
        if (response === null) {
            if (this._context.getProperty("cloudCoupon") === undefined)
                this._eventBus.emit(EventTypes.COUPON_INVALID, "InvalidCoupon");

            return null;
        }
        if (response.orderNo && response.orderNo !== 0)
            order.OrderNo = response.orderNo;

        if (response.orderId)
            order.OrderID = response.orderId;
        
        return couponCode.toUpperCase().startsWith("SUC_") ? couponCode : `SUC_${couponCode}`; // this needs to be modified in order to be marked 'Redeemed' in database after the order is placed 
    }

    private async _validateSingleUseCouponAsync(order: IOrder, couponCode: string, orderDate: Date, showErrorMsg: boolean) {
        const data = {
            couponCode: couponCode.replace(/-/g, ""),
            orderId: order.OrderID,
            storeKey: this._data.getProperty("StoreInfo").storeKey,
            orderDate: orderDate
        };

        const response = await this._orderingService.validatePromotionCode(data);
        if (!response.success) {
            if (!showErrorMsg)
                return null;

            if (response.message == "Promotion has been redeemed.") {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("CouponRedeemed"),
                    true
                );
            }
            if (response.message == "Promotion cannot be applied multiple times.") {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("CouponCannotApplyMultiple"),
                    true
                );
            }
            if (response.message == "Promotion cannot be applied to order scheduled past its expiration date.") {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("CouponFutureOrderExpire"),
                    true
                );
            }
            return null;
        }
        return response;
    }

    private async _invalidateSingleUseCoupon(order: IOrder, couponCode: string) {
        const data = {
            couponCode: couponCode,
            orderId: order.OrderID,
            storeKey: this._data.getProperty("StoreInfo").storeKey
        };

        return await this._orderingService.invalidatePromotionCode(data);
    }

    /**
     * The code's coupon ID for the Setting 207 pre-check, looked up only when the limit would block, so an
     * Override-207 code isn't refused unseen. -1 (an unresolved coupon; null means a loyalty reward) otherwise.
     */
    private async _resolveCouponIdForLimitCheckAsync(order: IOrder, couponCode: string, orderDate: Date): Promise<number> {
        const catalog = this._data.getProperty("Coupons") ?? [];
        if (!hasCouponCountedByLimit(catalog, order.Coupons ?? []) || isExemptFromOnlineCouponLimit(this._data.getProperty("Settings")?.CPNLIM ?? StoreCouponLimitType.NO, catalog, null))
            return -1;

        // Read-only lookups with errors off; single-use redemption and order-type changes only happen after the check.
        const storeId = this._data.getProperty("StoreInfo").storeId || this._data.getBrandProperty("StoreInfo").storeId;
        const coupon = await this._getCouponByCode(couponCode, orderDate, storeId, order.GUID)
            ?? await this._getCouponByPromotionCode(couponCode, storeId, false, order.GUID)
            ?? await this._getCouponByCampaignCode(couponCode, storeId, false, order.GUID);
        return coupon?.CouponId ?? -1;
    }

    private async _applySingleUseOrPromotionCodeAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string, orderDate: Date): Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> {
        const offer = this._user.getProperty("Offers")?.Codes?.find(x => x.CouponCode.toLowerCase() === Coupon.SanitizeSucCouponCode(couponCode)) ?? null;

        let result = await this._applyCouponByPromotionCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, false, orderDate, offer, true);

        if (!result.success && !offer){
            // Check if regular single use coupon next if it's not a promotion code or if promotion code application failed.
            result = await this._applyCouponByPromotionCodeAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, false, orderDate, null, true);
        }
        if (offer && result.success)
            offer.Used = true;

        return result;
    }

    private async _applyCouponByPromotionCodeAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string, error: boolean, orderDate: Date, offer: IUserCampaignCodesData | null, singleUseCoupon?: boolean): Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> {
        const storeId = !this._data.getProperty("StoreInfo").storeId ? this._data.getBrandProperty("StoreInfo").storeId : this._data.getProperty("StoreInfo").storeId;
        const coupon = await this._getCouponByPromotionCode(couponCode, storeId, error, order.GUID);
        if (!coupon)
            return { success: false, incompleteDiscount: null };

        if (singleUseCoupon){
            const validCouponCode = await this._redeemSingleUseCouponAsync(order, incompleteDiscounts, loyaltyProvider, couponCode, orderDate, true);
            if (validCouponCode)
                couponCode = validCouponCode;
            else
                return { success: false, incompleteDiscount: null };
        }

        const result = await this._applyCouponByCode_ContinueAsync(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, false, true, offer, false, false);
        return { success: result.success, incompleteDiscount: result.incompleteDiscount };
    }

    private async _applyCouponByCampaignCodeAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, couponCode: string, isOffer: boolean, error: boolean): Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> {
        const storeId = !this._data.getProperty("StoreInfo").storeId ? this._data.getBrandProperty("StoreInfo").storeId : this._data.getProperty("StoreInfo").storeId;
        const coupon = await this._getCouponByCampaignCode(couponCode, storeId, error, order.GUID);
        if (!coupon)
            return { success: false, incompleteDiscount: null };

        const offer = isOffer ? this._user.getProperty("Offers")?.Codes.find(x => x.CouponCode.toLowerCase() === couponCode) ?? null : null;

        const result = await this._applyCouponByCode_ContinueAsync(order, incompleteDiscounts, loyaltyProvider, coupon, couponCode, true, false, offer);

        if (offer && result.success)
            offer.Used = true;

        return { success: result.success, incompleteDiscount: result.incompleteDiscount };
    }

    private async _applyCouponByCode_ContinueAsync(order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, coupon: IDataCoupon, couponCode: string, isCampaignCode: boolean, isPromotionCode: boolean, offer: IUserCampaignCodesData | null, isSpecialCode: boolean = false, sucValidationReq?: boolean): Promise<{ success: boolean, incompleteDiscount: IIncompleteDiscount | null }> {
        if (!this.CheckCouponOTST(coupon, order.OrderTypeID)) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService
                    .names("InvalidOrderTypeCode")
                    .replace(
                        "??",
                        OnlineOrderingUtil.GetOrderTypeName(
                            order.OrderTypeID,
                            this._data.getProperty("OrderTypeSubTypes"),
                            this._localizationService
                        )
                    ),
                true
            );
            return { success: false, incompleteDiscount: null };
        } 
            
        if (!this.CheckCouponSCHS(order, coupon)) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("InvalidScheduleCode"),
                true
            );
            return { success: false, incompleteDiscount: null };
        }     

        if (!this.CheckCouponProfileRequired(coupon)) {
            this._eventBus.emit(EventTypes.COUPON_INVALID, "InvalidCoupon"); //message gets localized in cart.ts
            return { success: false, incompleteDiscount: null };
        }

        const params: ICheckCouponHasQualifyingItemsParams = {
            couponId: coupon.CouponId,
            couponCode: couponCode,
            orderId: null,
            reward: null,
            channelId: coupon.ChannelID,
            isCampaignCode: isCampaignCode,
            isPromotionCode: isPromotionCode,
            offer: offer,
            isSpecialCode: isSpecialCode,
            sucValidationReq: sucValidationReq
        };

        Util.setElement("value", "txt_cart_coupon_code", "");
        const incomplete = await this.CheckCouponHasQualifyingItems(this._eventBus, order, incompleteDiscounts, loyaltyProvider, params);
        return { success: true, incompleteDiscount: incomplete };
    }

    private _getCouponByCode = async (couponCode: string, orderDate: Date, storeId: number, guid: string): Promise<IDataCoupon | null> => {
        const data = {
            couponCode: couponCode,
            order_date: orderDate,
            storeId: storeId,
            is_app: Util.isAppView(),
            order_guid: guid
        };

        const response = await this._orderingService.getCouponByCouponCode(data);
        if (response.success && response.couponId != null) {
            const couponId = response.couponId;
            return this._data.getProperty("Coupons").find(x => x.CouponId === couponId) ?? null;
        }

        return null;
    }

    private _getCouponByPromotionCode = async (couponCode: string, storeId: number, error: boolean, guid: string): Promise<IDataCoupon | null> => {
        const data = {
            couponCode: couponCode,
            storeId: storeId,
            order_guid: guid
        };
        const response = await this._orderingService.getCouponByPromotionCode(data);

        if (response.success && response.coupon != "") {
            const coupon = this._mapCoupon(response.coupon);
            const coupons = this._data.getProperty("Coupons");
            if (!coupons.some(x => x.CouponId == coupon.CouponId))
                coupons.push(coupon);
            return coupon;
        } else if (!response.success && !response.serverError && error)
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("UnableToRetrieveSurveyCoupon"),
                true
            );

        return null;
    }

    private _getCouponByCampaignCode = async (couponCode: string, storeId: number, error: boolean, guid: string): Promise<IDataCoupon | null> => {
        const data = {
            couponCode: couponCode,
            storeId: storeId,
            order_guid: guid
        };
        const response = await this._orderingService.getCouponByCampaignCode(data);

        if (response.success && response.coupon != "") {
            const coupon = this._mapCoupon(response.coupon);
            const coupons = this._data.getProperty("Coupons");
            if (!coupons.some(x => x.CouponId == coupon.CouponId))
                coupons.push(coupon);
            return coupon;
        } else if (!response.success && !response.serverError && error)
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("UnableToRetrieveCampaignCoupon"),
                true
            );

        return null;
    }

    private readonly _changeOrderTypeForCoupon = async (order: IOrder, coupon: IDataCoupon, loyaltyProvider: ILoyaltyProvider, cloudCoupon: ICloudCoupon | null): Promise<boolean> => {
        if (this.CheckCouponOTST(coupon, order.OrderTypeID))
            return false;

        //change order type id to first available order type sub type for the coupon
        const couponOrderTypes = coupon.OrderTypeSubTypes.map(x => x.OrderTypeId) ?? [];
        const orderTypes = this._data.getBrandProperty("OrderTypeSubTypes").map(x => x.OrderTypeId) ?? [];

        let newOrderTypeId = 0;
        for (const ot of orderTypes) {
            if (couponOrderTypes.includes(ot)) {
                newOrderTypeId = ot;
                break;
            }
        }

        //if no available order type found, continue having coupon apply and display invalid coupon error message
        if (newOrderTypeId === 0)
            return false;

        if (cloudCoupon){
            this._context.setProperty("cloudCouponInProgress", true);
            this._context.setProperty("cloudCoupon", cloudCoupon);
        }
        this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, newOrderTypeId, loyaltyProvider);
        return true;
    }

    public RemoveNotApplicableOrderTypeCoupons = async (order: IOrder, incompleteDiscounts: IIncompleteDiscount[], loyaltyProvider: ILoyaltyProvider, orderTypeID: number): Promise<void> => {
        for (let i = order.Coupons.length - 1; i >= 0; i--) {
            let coupon = order.Coupons[i];
            let cpn = this.GetCoupon(coupon.CouponId, true, orderTypeID, coupon.ChannelID);
            if (cpn === null)
                await this.RemoveCouponByKey(order, incompleteDiscounts, loyaltyProvider, coupon.CouponKey, false, false);
        }
    }

    /**
     * Gets a list of all of an item's sizes by its item ID and group number
     * @param itemId - ID of the item
     * @param groupIndex - Group number
     * @returns {number[]} - Array of size IDs
     */
    public getCouponItemSizesByGroup(coupon: IDataCoupon | null, itemId: number, groupIndex: number): number[] {
        if (!coupon)
            return [];

        const items = coupon.Items.filter(x => x.GroupIndex === groupIndex && x.ItemId === itemId);
        return (items.length > 0) ? items.map(x => x.SizeId) : [];
    }

    /**
     * Calculates the meal deal price before applying the coupon to the order.
     * It clones the order to avoid modifying the original order.
     * @param order - The order to calculate the meal deal price for
     * @param coupon - The coupon to apply
     * @returns {number} - The meal deal price after applying the coupon
     */
    public async getCouponMealDealPrice(order: IOrder, coupon: IDataCoupon) {
        let orderClone = structuredClone(order);
        let qualifiedList = this._getQualifiedItemList(orderClone, coupon);

        let discountAmount = await this._getCouponDiscountAmount(orderClone, coupon, qualifiedList);

        let qualifiedItemsRegularPrice = qualifiedList.reduce((sum, item) => sum + (item.oPrice), 0);

        return qualifiedItemsRegularPrice - discountAmount;
    }

    private _allQualifiedAreHalves(qualifiedList: IvItem[]): boolean {
        return qualifiedList.every((x: IvItem) => x.HalfIndex > 0);
    }

    private async _getCouponDiscountAmount(order: IOrder, coupon: IDataCoupon, qualifiedList: IvItem[]): Promise<number> {
        let discountAmount = 0;
        let totalAmounts = this._getTotalCouponItemsPrices(qualifiedList);
        const cDiscount = coupon.Discount;

        switch (coupon.CouponTypeId) {
            case CouponTypeList.MANAGER_DISCOUNT:
            case CouponTypeList.NEXT_ORDER_CREDIT:
            case CouponTypeList.LOYALTY_DISCOUNT:
            case CouponTypeList.EMPLOYEE_DISCOUNT:
            case CouponTypeList.EMPLOYEE_CONSUMPTION:
                if (coupon.CouponId === CouponIdList.MANAGER_DOLLAR_OFF ||
                    coupon.CouponId === CouponIdList.LOYALTY_OFFER_THIRDPARTY ||
                    coupon.CouponId === CouponIdList.LOYALTY_REWARD_THIRDPARTY ||
                    coupon.CouponId === CouponIdList.MANAGER_NEXT_ORDER_CREDIT_DOLLAR_OFF
                ) {
                    discountAmount = cDiscount;
                } else if (
                    coupon.CouponId === CouponIdList.MANAGER_PERCENT_OFF_ORDER ||
                    coupon.CouponId === CouponIdList.EMPLOYEE_DISCOUNT ||
                    coupon.CouponId === CouponIdList.EMPLOYEE_CONSUMPTION ||
                    coupon.CouponId === CouponIdList.MANAGER_NEXT_ORDER_CREDIT_PERCENT_OFF
                ) {
                    discountAmount = (order.SubTotal - order.Discount + order.OrderTypeSubType.OrderTypeCharge) * cDiscount / 100;
                } else if (coupon.CouponId === CouponIdList.MANAGER_FIXED_PRICE) {
                    discountAmount = totalAmounts.OrderItemPrice - cDiscount;
                } else if (coupon.CouponId === CouponIdList.MANAGER_PERCENT_OFF_ITEM) {
                    discountAmount = (totalAmounts.OrderItemPrice * coupon.Discount / 100);
                } else if (coupon.CouponId === CouponIdList.LOYALTY_REDEEM_ITEM) {
                    discountAmount = totalAmounts.OrderItemPrice;
                }
                break;
            case CouponTypeList.DOLLAR_OFF:
                discountAmount = cDiscount - totalAmounts.CouponAddOnPrice;
                break;
            case CouponTypeList.FIXED_PRICE:
                if (qualifiedList) {
                    let cItemPrice = totalAmounts.CouponItemPrice;
                    //Bug 53347: The item price override will occur if the item price is found to be greater than the calculated qualified items order price OR
                    //if all the qualified items are halves AND item price is less than the calculated qualified items order price AND the coupon has exact match enabled.
                    //This handles the differences found when qualifying all halves due to the order of the qualified halves and if they have a difference in price, regardless of the
                    //half and half pricing strategy from setting 22.
                    if (coupon.ExactMatch && cItemPrice > totalAmounts.OrderItemPrice)
                        cItemPrice = totalAmounts.OrderItemPrice;
                    discountAmount = cItemPrice - cDiscount - totalAmounts.CouponAddOnPrice;
                }
                break;
            case CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER:
                discountAmount = (order.SubTotal - order.Discount) * cDiscount / 100;
                break;
            case CouponTypeList.PERCENTAGE_OFF_AN_ITEM:
                discountAmount = (totalAmounts.OrderItemPrice * cDiscount / 100) - totalAmounts.CouponAddOnPrice;
                break;
            case CouponTypeList.PERCENTAGE_OFF_AN_ITEM_IN_CATEGORY_OR_BOGO_IN_CATEGORY:
                discountAmount = totalAmounts.OrderItemPrice * cDiscount / 100;
                break;
            case CouponTypeList.PERCENTAGE_OFF_ALL_ITEMS_IN_CATEGORY:
                discountAmount = totalAmounts.OrderItemPrice * cDiscount / 100;
                break;
            case CouponTypeList.PERCENTAGE_OFF_ITEM_IN_CATEGORY_BASED_ON_PRICE:
                discountAmount = (totalAmounts.CouponItemPrice * cDiscount / 100) - totalAmounts.CouponAddOnPrice;
                break;
            case CouponTypeList.UPGRADE:
                discountAmount = await this._getDiscountAmountForUpgradeCoupon(order, coupon, qualifiedList);
                break;
            case CouponTypeList.QUANTITY_PRICE:
            case CouponTypeList.MIX_AND_MATCH_PRICE:
                discountAmount = this._getDiscountAmountForQuantityPriceCoupon(cDiscount, qualifiedList);
                break;
            case CouponTypeList.MIX_AND_MATCH_PERCENTAGE:
                discountAmount = this._getDiscountAmountForMixNMatchPercentageCoupon(cDiscount, qualifiedList);
                break;
            case CouponTypeList.QUANTITY_DISCOUNT:
                discountAmount = this._getDiscountAmountForQuantityDiscountCoupon(cDiscount, qualifiedList, totalAmounts);
                break;
        }
        discountAmount = Util.Float2(discountAmount);

        if (discountAmount > 0) {
            discountAmount = this._limitMaximumDiscountAmount(discountAmount, coupon, order);
        }

        if (discountAmount > 0) {
            this._splitDiscount(order, qualifiedList, totalAmounts.OrderItemPrice, discountAmount, coupon, cDiscount);
            //update discountAmount if only part of that amount was applied to the qualified items
            discountAmount = this._getTotalQualifiedItemsDiscountAmount(qualifiedList, discountAmount);
        }

        return discountAmount;
    }

    private async _getDiscountAmountForUpgradeCoupon(order: IOrder, coupon: IDataCoupon, qualifiedList: IvItem[]): Promise<number> {
        let discountAmount = 0;
        if (qualifiedList) {
            let price = 0;
            const qualItem = qualifiedList.length > 0 ? qualifiedList[0] : null;
            const halfCount = qualItem?.HalfCount ?? 1;
            // if half and half Item got qualified check both halfs for price
            if (qualifiedList.length > 1 && halfCount > 1) {
                price = await this._setHalfNHalfUpgradePrice(order, coupon, qualifiedList);
            } else if (qualItem) {
                price = this._getUpgradePrice(order, coupon, qualItem);
            }
            let orderItemPrice = 0;
            for (const qualItem of qualifiedList) {
                orderItemPrice += qualItem.oPrice;
            }
            if (price > 0) {
                discountAmount = Util.Float2(orderItemPrice - Util.Float2(price * qualifiedList.length));
            }
        }
        return discountAmount;
    }

    private _getUpgradePrice(order: IOrder, coupon: IDataCoupon, qualItem: IvItem): number {
        let price = 0;
        const oItem = order.Items[qualItem.ItemArrayIndex];
        for (const upgrade of coupon.Upgrades) {
            if (oItem.SizeId === upgrade.BuySizeId) {
                let nItem = Util.deepClone(oItem);
                nItem.SizeId = upgrade.PriceSizeId;

                const dependencies: IGetItemTaxDependencies = {
                    taxesData: this._data.getProperty("Taxes"),
                    zipTaxesData: this._data.getProperty("ZipTaxes"),
                    isCallCenter: this._data.getProperty("Settings").ISCC,
                    orderTypeId: order.OrderTypeID,
                    address: order.Address
                };

                try {
                    OnlineOrderingUtil.PriceItem(this._data, order.Items, nItem, dependencies, order.OrderTypeSubType.PriceGroupId);
                    price = nItem.Price;
                } catch (e) {
                    if (!(e instanceof ItemPricingUnavailableError))
                        throw e;
                    // This item doesn't have a price configured for the upgrade's target size -- not an error, just not a valid upgrade.
                }
            }
            if (price > 0)
                break;
        }
        return price;
    }

    private _getDiscountAmountForQuantityPriceCoupon(cDiscount: number, qualifiedList: IvItem[]): number {
        let discountAmount = 0;
        for (const item of qualifiedList) {
            if (item.HalfCount && item.cPrice > (cDiscount / item.HalfCount)) {
                const pendingDiscountAmt = (item.cPrice - (cDiscount / item.HalfCount) - item.AddOn);
                //adjust discount amount if order price will fall below the set price point
                if ((item.oPrice - pendingDiscountAmt) < (cDiscount / item.HalfCount)) {
                    discountAmount += (item.oPrice - (cDiscount / item.HalfCount) - item.AddOn);
                } else {
                    discountAmount += pendingDiscountAmt;
                }
            }
        }
        return discountAmount;
    }

    private _getDiscountAmountForMixNMatchPercentageCoupon(cDiscount: number, qualifiedList: IvItem[]): number {
        let discountAmount = 0;
        for (const item of qualifiedList) {
            if (item.oPrice > 0) {
                discountAmount += (item.oPrice * cDiscount) / 100 - item.AddOn;
            }
        }
        return discountAmount;
    }

    private _getDiscountAmountForQuantityDiscountCoupon(cDiscount: number, qualifiedList: IvItem[], totalAmounts: ITotalCouponPrice): number {
        let discountAmount = 0;
        if (qualifiedList) {
            for (const item of qualifiedList) {
                if (item.HalfCount && item.cPrice > (cDiscount / item.HalfCount)) {
                    discountAmount += (cDiscount / item.HalfCount);
                }
            }
            discountAmount = discountAmount - totalAmounts.CouponAddOnPrice;
        }
        return discountAmount;
    }

    private _limitMaximumDiscountAmount(discountAmount: number, coupon: IDataCoupon, order: IOrder): number {
        if (discountAmount > 0) {
            const maxDiscount = coupon.MaximumDiscountAmount;
            if (maxDiscount > 0 && discountAmount > maxDiscount)
                discountAmount = maxDiscount;

            if (discountAmount > order.SubTotal - order.Discount + order.OrderTypeSubType.OrderTypeCharge)
                discountAmount = order.SubTotal - order.Discount + order.OrderTypeSubType.OrderTypeCharge;
        }
        return discountAmount;
    }
}